/**
 * @file emails.ts
 * @description 邮件管理页面组件，CRM 系统中最大的前端模块之一。
 * 该文件导出 `CrmEmailsPage` Lit Web Component（自定义元素 `<crm-emails-page>`），
 * 是整个邮件功能的主入口页面，负责邮件列表浏览、文件夹切换、标签筛选、
 * 邮件详情查看、回复/转发/草稿编辑、附件上传、收件人搜索、线程（对话）视图等全部交互逻辑。
 *
 * 主要职责：
 * - 邮件列表展示与分页（收件箱 / 发件箱 / 已发送 / 草稿箱 / 垃圾箱 / 归档）
 * - 邮件文件夹侧边栏，支持未读数显示与星标邮件过滤
 * - 标签管理（创建 / 编辑 / 删除 / 按标签筛选）
 * - 邮件详情查看面板，含内联回复（Reply / Reply All / Forward）与草稿编辑
 * - 对话（线程）视图，将同 thread_id 的邮件聚合展示
 * - 收件人 / 抄送人的 chip 输入与客户匹配搜索
 * - 附件上传进度跟踪与附件管理
 * - 邮件签名（Signature）的加载与插入 / 移除
 * - 批量操作（标记已读、删除等）
 *
 * 关键设计决策：
 * - 所有邮件数据通过 `emailStore`（Observable Store）集中管理，组件通过订阅 store
 *   状态来驱动渲染，而非直接在组件内管理全部数据。
 * - 回复/转发 UI 嵌入在 `crm-side-panel`（viewer-panel）中，而非弹出独立窗口，
 *   保证用户在查看邮件的同时可以编写回复。
 * - 独立撰写邮件使用另一个 `crm-side-panel`（compose-panel），底层为
 *   `crm-email-compose-panel` 子组件。
 * - 收件人/抄送使用 chip 输入组件（`renderChipInput`），支持手动输入邮箱地址
 *   和搜索匹配的客户邮箱。
 * - 组件大量依赖 CustomEvent 与子组件通信（如 `crm-attachment-*`、
 *   `crm-email-reply` 等），通过 `addEventListener` 在 `connectedCallback` 注册。
 *
 * 与其他模块的关系：
 * - 依赖 `emailStore`（`../store.js`）作为数据层。
 * - 依赖多个子组件：`email-viewer`、`email-list-panel`、`email-compose-panel`、
 *   `crm-side-panel`、`crm-rich-editor`、`crm-signature-selector` 等。
 * - 工具函数来自 `../utils/email-helpers.js` 和 `../utils/index.js`。
 * - API 调用集中在 `../api.js`（`emailSendApi`、`emailApi`、`emailThreadApi`、`followUpApi`）。
 */

import { css, html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, state } from "lit/decorators.js";
// 邮件数据的 Observable Store，提供邮件列表、账户、标签等状态管理
import { emailStore } from "../store.js";
// 基础元素类，提供 Lit 组件的公共能力（如 shadow DOM 封装、公共样式等）
import { BaseElement } from "../base-element.js";
// 类型定义，描述邮件、客户、账户等数据结构
import type {
  Customer,
  Email,
  EmailAccount,
  EmailAddress,
  EmailTag,
  EmailSendRecord,
  EmailTask,
  ThreadSummary,
} from "../types.js";
// 以下为页面中使用的各子组件导入（带 .js 后缀是项目约定，确保 Vite 正确解析）
import "../components/email-viewer.js";
import "../components/email-list-panel.js";
import "../components/side-panel.js";
import "../components/signature-selector.js";
import "../components/rich-editor.js";
import "../components/tooltip.js";
import "../components/email-compose-panel.js";
import "../components/quick-create-customer.js";
import "../components/customer-view-modal.js";
// 全局 toast 通知组件
import { toast } from "../components/toast.js";
import "../components/table-toolbar.js";
import "../components/loading-overlay.js";
import "../components/pagination.js";
import "../components/action-menu.js";
// ActionMenu 的菜单项类型定义
import type { MenuItem } from "../components/action-menu.js";
// API 层：邮件发送、邮件操作、线程、跟进提醒等接口
import { emailSendApi, emailApi, emailThreadApi, followUpApi, emailTaskApi, API_BASE_URL } from "../api.js";
// 确认对话框组件
import { showConfirm } from "../components/confirm-dialog.js";
// 邮件相关工具函数：判断是否为自己发送、提取编辑器内容（去签名）、包裹签名 HTML、附件移除、收件人匹配与搜索等
import { isSentByMe, editorContentWithoutSignature, wrapSignatureHTML, removeAttachmentByIndex, fetchRecipientMatches, handleRecipientChipClick, searchRecipientOptions, buildForwardHeaderHtml, buildQuoteCitation, buildQuoteCitationHtml, buildQuoteHeaderHtml, buildQuoteBodyHtml } from "../utils/email-helpers.js";
// 通用工具：网络错误判断、文件大小格式化
import { isNetworkError, formatFileSize, sanitizeEmailHtml } from "../utils/index.js";

/**
 * 邮件管理页面组件。
 *
 * 该组件是 CRM 系统中邮件模块的主页面，集成了邮件列表、文件夹导航、标签管理、
 * 邮件详情查看、回复/转发/草稿编辑、附件上传、收件人搜索、线程视图等全部功能。
 *
 * 数据来源：通过订阅 `emailStore` 获取邮件列表、账户列表、标签列表、文件夹计数等数据。
 * 交互方式：通过 CustomEvent 与子组件（email-viewer、email-list-panel、email-compose-panel 等）通信。
 *
 * @customElement crm-emails-page
 */
@customElement("crm-emails-page")
export class CrmEmailsPage extends BaseElement {
  // ==================== 邮件列表与加载状态 ====================

  /** 当前文件夹下的邮件列表 */
  @state() private emails: Email[] = [];
  /** 邮件列表是否正在加载中 */
  @state() private isLoading = false;
  /** 是否正在加载邮件详情（控制 viewer-panel 的 loading 遮罩） */
  @state() private isViewingEmail = false;
  /** 当前文件夹下的邮件总数（用于分页） */
  @state() private total = 0;
  /** 当前页码（从 1 开始） */
  @state() private currentPage = 1;
  /** 每页显示的邮件数量 */
  @state() private perPage = 20;
  /** 左侧显示的进行中邮件任务（最多 5 个） */
  @state() private runningTasks: EmailTask[] = [];
  /** 当前选中的邮件任务；选中后在右侧展示发送记录 */
  @state() private selectedTask: EmailTask | null = null;
  /** 当前任务的发送记录 */
  @state() private taskRecords: EmailSendRecord[] = [];
  /** 当前任务发送记录总数 */
  @state() private taskRecordsTotal = 0;
  /** 当前任务发送记录页码 */
  @state() private taskRecordsPage = 1;
  /** 发送记录加载状态 */
  @state() private taskRecordsLoading = false;
  /** 当前选中的发送记录，展示实际发送正文 */
  @state() private selectedTaskRecord: EmailSendRecord | null = null;
  /** 进行中任务轮询定时器 */
  private runningTasksTimer?: number;

  // ==================== 过滤器 ====================

  /** 已读过滤：0=未读, 1=已读, null=全部。默认显示未读（仅收件箱时生效） */
  @state() private filterRead: number | null = 0;
  /** 按邮件账户 ID 过滤，null 表示不过滤（显示所有账户的邮件） */
  @state() private filterAccountId: number | null = null;
  /** 所有邮件账户列表 */
  @state() private accounts: EmailAccount[] = [];
  /** 当前选中的发送账户 ID（用于回复/转发时选择发件账户） */
  @state() private selectedAccount: number | null = null;
  /** 激活签名请求序号，避免切换账户后插入旧账户签名。 */
  private _signatureRequestId = 0;
  /** 邮件详情请求序号，避免快速切换时旧详情覆盖当前邮件。 */
  private _viewerRequestId = 0;

  // ==================== 线程（对话）视图 ====================

  /** 视图模式：list=邮件列表, thread=对话（线程）列表 */
  @state() private viewMode: "list" | "thread" = "list";
  /** 对话列表数据 */
  @state() private threads: ThreadSummary[] = [];
  /** 对话总数（用于线程分页） */
  @state() private threadTotal = 0;
  /** 对话列表当前页码 */
  @state() private threadPage = 1;
  /** 当前查看的线程 ID，null 表示未查看线程详情 */
  @state() private selectedThreadId: string | null = null;
  /** 当前线程内的邮件列表 */
  @state() private threadEmails: Email[] = [];
  /** 线程内各邮件的展开/折叠状态，key 为邮件在线程数组中的索引 */
  @state() private expandedThreadEmails: Record<number, boolean> = {};

  // ==================== 收件人 chips（回复/转发用） ====================

  /** 收件人邮箱地址列表（chip 形式） */
  @state() private toRecipients: string[] = [];
  /** 收件人邮箱与客户对象的匹配映射，key 为邮箱地址（小写） */
  @state() private matchedMap: Map<string, Customer> = new Map();
  /** 是否为"回复全部"模式 */
  @state() private isReplyAll = false;
  /** 当前选中邮件的 ID 集合（用于批量操作） */
  @state() private selectedEmailIds: Set<number> = new Set();
  /** 收件人输入框的当前值 */
  @state() private toInput = "";
  /** 收件人搜索下拉选项 */
  @state() private toOptions: { label: string; value: string }[] = [];
  /** 是否显示收件人搜索下拉 */
  @state() private showToDropdown = false;
  /** 收件人搜索的防抖定时器 ID */
  private toTimer: number | null = null;

  // ==================== 抄送 chips ====================

  /** 是否展开抄送输入区域 */
  @state() private showCc = false;
  /** 抄送人邮箱地址列表 */
  @state() private ccRecipients: string[] = [];
  /** 抄送人输入框的当前值 */
  @state() private ccInput = "";
  /** 抄送人搜索下拉选项 */
  @state() private ccOptions: { label: string; value: string }[] = [];
  /** 是否显示抄送人搜索下拉 */
  @state() private showCcDropdown = false;
  /** 抄送人搜索的防抖定时器 ID */
  private ccTimer: number | null = null;

  // ==================== 邮件详情与附件 ====================

  /** 当前在详情面板中查看的邮件对象 */
  @state() private selectedEmail: Email | null = null;
  /** 正在上传中的附件列表（显示上传进度） */
  @state() private uploadingAttachments: Array<{
    name: string;
    size: number;
    progress: number;
  }> = [];
  /** 已上传完成的附件列表 */
  @state() private attachedFiles: Array<{
    id: number;
    filename: string;
    size: number;
  }> = [];

  // ==================== 回复 / 转发 / 草稿状态 ====================

  /** 当前回复的原始邮件对象，null 表示不在回复状态 */
  @state() private replyingTo: Email | null = null;
  /** 回复邮件的主题（自动加 "Re:" 或 "Fwd:" 前缀） */
  @state() private replySubject = "";
  /** 是否正在编辑回复主题（点击主题切换为输入框） */
  @state() private editingSubject = false;
  /** 是否为转发模式 */
  @state() private isForward = false;
  /** 转发时的编辑器初始内容（空行占位） */
  @state() private forwardContent = "";

  // ==================== 撰写面板（compose-panel）草稿状态 ====================
  // 以下字段用于独立撰写邮件面板（非内联回复），通过 compose-panel 子组件管理

  /** 草稿邮件 ID，0 表示新邮件 */
  @state() private composeDraftId = 0;
  /** 草稿收件人邮箱（单输入框模式） */
  @state() private composeDraftToEmail = "";
  /** 草稿编辑器内容 */
  @state() private composeDraftContent = "";
  /** 草稿收件人列表（chip 模式） */
  @state() private composeDraftTo: string[] = [];
  /** 草稿抄送人列表 */
  @state() private composeDraftCc: string[] = [];
  /** 草稿使用的发送账户 ID */
  @state() private composeDraftAccountId = 0;
  /** 草稿附件列表 */
  @state() private composeDraftAttachments: Array<{ id: number; filename: string; size: number }> = [];
  /** 转发邮件的头部信息 HTML（在 compose-panel 中展示） */
  @state() private composeForwardHeader = "";
  /** 转发邮件的原始内容 HTML（在 compose-panel 中展示） */
  @state() private composeForwardHtml = "";
  /** 撰写会话计数器，每次打开新撰写/草稿时递增，用于通知 compose-panel 重置状态 */
  @state() private _composeSession = 0;

  // ==================== 文件夹与标签 ====================

  /** 当前文件夹名称（inbox/outbox/sent/drafts/trash/archive） */
  @state() private currentFolder = "inbox";
  /** 各文件夹的邮件总数和未读数 */
  @state() private folderCounts: Array<{
    folder: string;
    total: number;
    unread: number;
  }> = [];

  // ==================== 标签管理 ====================

  /** 所有邮件标签列表 */
  @state() private allTags: EmailTag[] = [];
  /** 当前过滤的标签 ID，null 表示不过滤标签 */
  @state() private filterTagId: number | null = null;
  /** 是否显示标签管理弹窗 */
  @state() private showTagManager = false;
  /** 新建标签的名称 */
  @state() private newTagName = "";
  /** 新建标签的颜色（默认蓝色） */
  @state() private newTagColor = "#0d74ce";
  /** 当前正在编辑的标签 ID */
  @state() private editingTagId: number | null = null;
  /** 编辑中标签的名称 */
  @state() private editTagName = "";
  /** 编辑中标签的颜色 */
  @state() private editTagColor = "";
  /** 从撰写面板触发的快速创建客户时的预填邮箱 */
  @state() private quickCreateEmail = "";

  /** 编辑器空白内容的默认占位：8 个空行 div，用于回复/转发时给用户留出书写空间 */
  private static DEFAULT_EMPTY_LINES = "<div><br></div>".repeat(8);

  /**
   * 组件样式定义。
   * 主要包含自定义 checkbox 样式（`.crm-checkbox`），用于邮件列表中的全选/单选复选框。
   * 支持三种状态：未选中、选中（checked，显示对勾图标）、半选中（indeterminate，显示横线图标）。
   */
  static styles = css`
    :host {
      display: block;
      height: 100%;
    }

    .task-progress-bar {
      background-size: 200% 100%;
      animation: task-progress-flow 1.4s linear infinite;
    }
    .task-progress-bar.success {
      background-image: linear-gradient(90deg, #0d74ce 0%, #52a7ef 50%, #0d74ce 100%);
    }
    .task-progress-bar.error {
      background-image: linear-gradient(90deg, #d84a4a 0%, #f28b8b 50%, #d84a4a 100%);
    }
    @keyframes task-progress-flow {
      from { background-position: 100% 0; }
      to { background-position: -100% 0; }
    }

    /* 自定义复选框基础样式：16x16 方形，圆角 4px */
    .crm-checkbox {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border: 1px solid var(--tw-color-border, #e0e1e6);
      border-radius: 4px;
      background: var(--tw-color-surface, #ffffff);
      cursor: pointer;
      transition:
        border-color 0.15s,
        background 0.15s;
      flex-shrink: 0;
      position: relative;
    }
    .crm-checkbox:hover {
      border-color: var(--tw-color-text-muted, #b0b4ba);
    }
    /* 选中 / 半选中状态：背景变为主色调 */
    .crm-checkbox.checked,
    .crm-checkbox.indeterminate {
      background: var(--tw-color-primary, #000000);
      border-color: var(--tw-color-primary, #000000);
    }
    /* 选中状态：内嵌 SVG 对勾图标 */
    .crm-checkbox.checked {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
      background-size: 10px 10px;
      background-position: center;
      background-repeat: no-repeat;
    }
    /* 半选中状态：内嵌 SVG 横线图标 */
    .crm-checkbox.indeterminate {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3'%3E%3Cline x1='5' y1='12' x2='19' y2='12'/%3E%3C/svg%3E");
      background-size: 10px 10px;
      background-position: center;
      background-repeat: no-repeat;
    }
    /* 隐藏原生 input，仅保留自定义外观 */
    .crm-checkbox input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }
  `;

  /** emailStore 的取消订阅函数，在 disconnectedCallback 中调用以避免内存泄漏 */
  private _unsubscribe?: () => void;

  /**
   * 组件挂载到 DOM 时的生命周期回调。
   * 主要完成以下初始化工作：
   * 1. 订阅 `emailStore` 状态变化，将 store 数据同步到组件的 @state 字段以触发重新渲染。
   * 2. 加载初始邮件列表。
   * 3. 获取邮件账户列表、文件夹计数、标签列表。
   * 4. 注册各子组件发出的事件监听器（附件上传、邮件选择、回复/转发、快捷回复等）。
   */
  connectedCallback() {
    super.connectedCallback();
    // 订阅 emailStore 的状态变化，将 store 中的数据同步到组件 @state 字段
    this._unsubscribe = emailStore.subscribe((state) => {
      this.emails = state.emails;
      this.accounts = state.accounts;
      this.isLoading = state.isLoading;
      this.total = state.total;
      this.currentPage = state.currentPage;
      this.perPage = state.perPage;
      this.threads = state.threads;
      this.threadEmails = state.threadEmails;
      this.threadTotal = state.threadTotal;
      this.currentFolder = state.currentFolder;
      this.folderCounts = state.folderCounts;
      this.allTags = state.tags;
      this.filterTagId = state.currentTagId;
    });
    // 初始加载：邮件列表、账户、文件夹计数、标签
    this.loadEmails();
    emailStore.fetchAccounts();
    emailStore.fetchFolderCounts();
    emailStore.fetchTags();
    void this.loadRunningTasks();
    this.runningTasksTimer = window.setInterval(() => void this.loadRunningTasks(), 30_000);
    // 深链打开：URL 携带 email_id 参数时（如从首页日历跳转）自动打开该邮件详情
    void this.openEmailFromQuery();
    // 注册附件上传相关事件监听（由 email-compose-panel 等子组件触发）
    this.addEventListener("crm-attachment-start", this.onAttachmentStart);
    this.addEventListener("crm-attachment-progress", this.onAttachmentProgress);
    this.addEventListener("crm-attachment-complete", this.onAttachmentComplete);
    this.addEventListener("crm-attachment-error", this.onAttachmentError);
    // 注册邮件操作相关事件监听（由 email-viewer / email-list-panel 等子组件触发）
    this.addEventListener("crm-email-select", this.onPanelEmailSelect);
    this.addEventListener("crm-email-reply", this.onEmailReply);
    this.addEventListener("crm-email-reply-all", this.onEmailReplyAll);
    this.addEventListener("crm-email-forward", this.onEmailForward);
    this.addEventListener("crm-quick-reply", this.onQuickReply);
  }

  /**
   * 组件从 DOM 中移除时的生命周期回调。
   * 负责清理工作：取消 store 订阅、清除防抖定时器、移除所有事件监听器，
   * 防止内存泄漏和无效回调。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    // 取消 store 订阅，避免组件销毁后仍被回调
    this._unsubscribe?.();
    // 清除收件人/抄送搜索的防抖定时器
    if (this.toTimer) clearTimeout(this.toTimer);
    if (this.ccTimer) clearTimeout(this.ccTimer);
    if (this.runningTasksTimer) window.clearInterval(this.runningTasksTimer);
    // 移除所有在 connectedCallback 中注册的事件监听器
    this.removeEventListener("crm-attachment-start", this.onAttachmentStart);
    this.removeEventListener("crm-attachment-progress", this.onAttachmentProgress);
    this.removeEventListener("crm-attachment-complete", this.onAttachmentComplete);
    this.removeEventListener("crm-attachment-error", this.onAttachmentError);
    this.removeEventListener("crm-email-select", this.onPanelEmailSelect);
    this.removeEventListener("crm-email-reply", this.onEmailReply);
    this.removeEventListener("crm-email-reply-all", this.onEmailReplyAll);
    this.removeEventListener("crm-email-forward", this.onEmailForward);
    this.removeEventListener("crm-quick-reply", this.onQuickReply);
  }

  /**
   * 从 emailStore 加载当前文件夹下的邮件列表。
   * 根据 currentPage、perPage、currentFolder 以及各种过滤器（已读、标签、账户）
   * 组装查询参数，调用 store 的 fetchEmails 方法。
   */
  private async loadEmails() {
    await emailStore.fetchEmails({
      page: this.currentPage,
      per_page: this.perPage,
      folder: this.currentFolder,
      // 仅在没有标签过滤且 filterRead 非空时传递已读过滤
      is_read: this.filterTagId === null && this.filterRead !== null ? this.filterRead : undefined,
      tag_id: this.filterTagId ?? undefined,
      account_id: this.filterAccountId ?? undefined,
    });
  }

  /** 加载进行中的邮件任务，供邮件页左侧快捷入口使用。 */
  private async loadRunningTasks() {
    try {
      const response = await emailTaskApi.list({ status: 1, page: 1, per_page: 5 });
      this.runningTasks = response.data.slice(0, 5);
      if (this.selectedTask) {
        const refreshed = this.runningTasks.find((task) => task.id === this.selectedTask?.id);
        if (refreshed) this.selectedTask = refreshed;
      }
    } catch {
      // 邮件任务入口是辅助信息，加载失败时不影响邮件列表使用。
    }
  }

  /** 选中任务并加载其发送记录。 */
  private async selectTask(task: EmailTask) {
    this.selectedTask = task;
    this.selectedTaskRecord = null;
    this.taskRecordsPage = 1;
    await this.loadTaskRecords();
  }

  /** 分页加载当前任务的发送记录。 */
  private async loadTaskRecords() {
    if (!this.selectedTask) return;
    this.taskRecordsLoading = true;
    try {
      const response = await emailTaskApi.getSendRecords(this.selectedTask.id, {
        page: this.taskRecordsPage,
        per_page: 20,
      });
      this.taskRecords = response.data;
      this.taskRecordsTotal = response.pagination.total;
    } finally {
      this.taskRecordsLoading = false;
    }
  }

  /** 清除任务详情，恢复邮件列表。 */
  private clearSelectedTask() {
    this.selectedTask = null;
    this.selectedTaskRecord = null;
    this.taskRecords = [];
    this.taskRecordsTotal = 0;
  }

  /**
   * 处理分页页码变化事件。
   * 更新当前页码、清空选中状态、重新加载邮件列表。
   *
   * @param page - 新的页码（从 1 开始）
   */
  private handlePageChange(page: number) {
    this.currentPage = page;
    this.clearSelection();
    this.loadEmails();
  }

  /**
   * 切换单封邮件的选中状态（用于批量操作）。
   * 通过创建新的 Set 对象来触发 Lit 的响应式更新。
   *
   * @param id - 邮件 ID
   */
  private toggleEmailSelection(id: number) {
    const next = new Set(this.selectedEmailIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.selectedEmailIds = next;
  }

  /**
   * 全选 / 取消全选当前页面的所有邮件。
   * 如果当前已全部选中则取消全选，否则全选当前页所有邮件。
   */
  private toggleSelectAll() {
    if (
      this.selectedEmailIds.size === this.emails.length &&
      this.emails.length > 0
    ) {
      this.selectedEmailIds = new Set();
    } else {
      this.selectedEmailIds = new Set(this.emails.map((e) => e.id));
    }
  }

  /**
   * 批量标记选中邮件为已读。
   * 调用 store 的 markMultipleAsRead 批量接口，完成后清空选中状态并提示用户。
   */
  private async handleBatchMarkRead() {
    const ids = [...this.selectedEmailIds];
    try {
      await emailStore.markMultipleAsRead(ids);
      toast.info(`已标记 ${ids.length} 封为已读`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "操作失败";
      // 网络错误不弹出 toast（由全局错误处理统一处理）
      if (!isNetworkError(msg)) toast.error(msg);
    }
    this.selectedEmailIds = new Set();
  }

  /** 清空所有选中的邮件 ID */
  private clearSelection() {
    this.selectedEmailIds = new Set();
  }

  /**
   * 打开独立撰写邮件面板（compose-panel）。
   * 重置所有草稿相关状态字段，递增 _composeSession 以通知 compose-panel 子组件
   * 开始新的撰写会话，然后通过 side-panel 的 show 方法弹出面板。
   */
  private openPanel() {
    this.replyingTo = null;
    this.replySubject = "";
    this.composeDraftId = 0;
    this.composeDraftToEmail = "";
    this.composeDraftContent = "";
    this.composeDraftTo = [];
    this.composeDraftCc = [];
    this.composeDraftAccountId = 0;
    this.composeDraftAttachments = [];
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    // 递增会话计数器，通知 compose-panel 重置内部状态
    this._composeSession++;
    const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
    // 弹出 side-panel：标题"写邮件"，宽度 1080px，从底部滑出
    panel.show("写邮件", 1080, "bottom");
  }

  /**
   * 从富文本编辑器中提取内容并去除签名部分。
   * 代理调用 `email-helpers.js` 中的同名工具函数。
   *
   * @param editor - crm-rich-editor 组件实例
   * @returns 去除签名后的编辑器 HTML 内容
   */
  private editorContentWithoutSignature(editor: any): string {
    return editorContentWithoutSignature(editor);
  }

  /**
   * 将签名内容包裹在带标识的 div 中，便于后续识别和移除。
   * 代理调用 `email-helpers.js` 中的同名工具函数。
   *
   * @param sigId - 签名 ID
   * @param sigContent - 签名 HTML 内容
   * @returns 包裹后的签名 HTML 字符串
   */
  private wrapSignatureHTML(sigId: number, sigContent: string): string {
    return wrapSignatureHTML(sigId, sigContent);
  }

  /**
   * 从编辑器中移除已有签名。
   * 调用编辑器组件暴露的 stripSignature 方法（基于 TipTap 文档 API）。
   */
  private removeSignatureFromEditor() {
    // 优先从 viewer-panel 中查找编辑器（内联回复场景），否则全局查找
    const editor = (this.shadowRoot!.querySelector(
      "#viewer-panel crm-rich-editor",
    ) || this.shadowRoot!.querySelector("crm-rich-editor")) as any;
    editor?.stripSignature?.();
  }

  /**
   * 将签名插入到富文本编辑器中。
   * 先将签名中的 cid: 内联图片引用转换为 API URL（使其在编辑器中可见），
   * 然后移除旧签名并追加新签名。
   *
   * @param editor - crm-rich-editor 组件实例
   * @param accountId - 邮件账户 ID（用于构建签名图片 URL）
   * @param sigId - 签名 ID
   * @param sigContent - 签名的 HTML 内容
   */
  private insertSignatureToEditor(
    editor: any,
    accountId: number,
    sigId: number,
    sigContent: string,
  ) {
    // 编辑器可能尚未渲染或签名为空时直接返回
    if (!editor /* editor may not be rendered yet */ || !sigContent) return;
    // 将 cid:xxx 格式的内联图片引用转换为 API URL，使签名中的图片在编辑器中可见
    const displayContent = sigContent.replace(
      /(src|url)\s*=\s*["']cid:([^"']+)["']/gi,
      (_m: string, attr: string, cid: string) =>
        `${attr}="${API_BASE_URL}/emails/signature-images/${accountId}/${sigId}/${cid}"`,
    );
    // 通过 TipTap 文档 API 移除旧签名（可靠方式）
    editor.stripSignature?.();
    const sigBlock = this.wrapSignatureHTML(sigId, displayContent);
    // 将签名追加到编辑器内容末尾
    editor.value = (editor.value || "") + sigBlock;
  }

  /** 关闭撰写邮件面板（compose-panel） */
  private closePanel() {
    const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
    panel?.close();
  }

  /**
   * 处理从撰写面板触发的"快速创建客户"事件。
   * 将邮箱地址存入 quickCreateEmail 状态，触发快速创建客户弹窗显示。
   *
   * @param e - CustomEvent，detail.email 为待创建客户的邮箱地址
   */
  private _onCreateCustomerFromCompose(e: CustomEvent) {
    const { email } = e.detail as { email: string };
    this.quickCreateEmail = email;
  }

  /**
   * 客户创建成功后的回调。
   * 清空 quickCreateEmail（关闭弹窗），并通知 compose-panel 刷新收件人客户匹配。
   */
  private _onCustomerCreated() {
    this.quickCreateEmail = "";
    // 刷新 compose-panel 中的客户匹配结果
    const composePanel = this.shadowRoot!.querySelector("crm-email-compose-panel") as any;
    composePanel?.refreshCustomerMatch?.();
  }

  /** 取消快速创建客户，关闭弹窗 */
  private _onQuickCreateCancel() {
    this.quickCreateEmail = "";
  }

  /**
   * 处理"回复"事件（由 email-viewer 子组件通过 CustomEvent 触发）。
   * 将事件中的邮件数据传递给 startReply 方法，在详情面板中展开内联回复 UI。
   *
   * @param e - CustomEvent，detail 为要回复的邮件对象
   */
  private onEmailReply = (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    this.startReply(email);
  };

  /**
   * 处理"回复全部"事件。
   * 将邮件传递给 startReplyAll，在详情面板中展开内联回复全部 UI。
   *
   * @param e - CustomEvent，detail 为要回复全部的邮件对象
   */
  private onEmailReplyAll = (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    this.startReplyAll(email);
  };

  /**
   * 处理"转发"事件。
   * 根据当前是否有打开的详情面板决定转发方式：
   * - 详情面板打开时：使用内联转发（startForwardInline），在面板中直接编辑
   * - 否则：使用独立转发窗口（handleForward），打开 compose-panel
   *
   * @param e - CustomEvent，detail 为要转发的邮件对象
   */
  private onEmailForward = (e: Event) => {
    const email = (e as CustomEvent).detail as Email;
    // 如果详情面板打开，使用内联转发；否则打开独立转发窗口
    if (this.selectedEmail) {
      this.startForwardInline(email);
    } else {
      this.handleForward(email);
    }
  };

  /**
   * 处理"快捷回复"事件（由 email-viewer 的快捷回复输入框触发）。
   * 直接通过 API 发送简单回复邮件，无需打开完整编辑器。
   * 快捷回复将文本内容转为 HTML 段落，自动设置引用头和线程信息。
   *
   * @param e - CustomEvent，detail 包含 email（回复的邮件对象）和 content（回复文本内容）
   */
  private onQuickReply = async (e: Event) => {
    const { email, content } = (e as CustomEvent).detail as {
      email: Email;
      content: string;
    };
    try {
      await emailSendApi.send({
        account_id: email.account_id,
        to_email: [email.send_from?.email || ""],
        subject: `Re: ${email.subject || ""}`,
        // 将纯文本换行转为 HTML <br> 标签
        content: `<p>${content.replace(/\n/g, "<br>")}</p>`,
        content_type: "html",
        in_reply_to: email.message_id || "",
        // 构建邮件引用链：原始 references + 当前 message_id
        references: email.references
          ? `${email.references} ${email.message_id}`
          : email.message_id || "",
        thread_id: email.thread_id || email.message_id || "",
      });
      toast.info("快捷回复已发送");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "发送失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  };

  /**
   * 开始回复邮件（单回复，仅回复发件人）。
   * 设置回复状态字段，自动填充收件人为原邮件发件人，主题加 "Re:" 前缀。
   * 完成后聚焦编辑器等待用户输入。
   *
   * @param email - 要回复的原始邮件对象
   */
  private startReply(email: Email) {
    this.replyingTo = email;
    this.isReplyAll = false;
    // 自动选择原邮件所在的账户作为发送账户
    this.selectedAccount = email.account_id || null;
    // 回复自己发出的邮件时，收件人指向原收件人列表（而非发给自己）
    this.toRecipients = isSentByMe(email)
      ? (email.send_to || []).map((a) => a.email).filter(Boolean)
      : email.send_from?.email
        ? [email.send_from.email]
        : [];
    this.ccRecipients = [];
    this.showCc = false;
    this.toInput = "";
    this.ccInput = "";
    // 自动添加 "Re:" 前缀（如果原主题已有 Re: 则不再重复添加）
    this.replySubject = `Re: ${email.subject || ""}`;
    this.uploadingAttachments = [];
    this.attachedFiles = [];
    this.editingSubject = false;
    // 查找收件人对应的客户匹配信息
    this.fetchMatches();
    // 在下一帧聚焦编辑器（等待 DOM 更新完成）
    requestAnimationFrame(() => {
      const vp = this.shadowRoot!.querySelector("#viewer-panel");
      (vp?.querySelector("crm-rich-editor") as any)?.focusEditor();
    });
  }

  /**
   * 开始回复全部邮件。
   * 收件人包含原发件人和所有原始收件人，抄送包含所有原始抄送人。
   * 自动从收件人列表中移除当前账户自己的邮箱地址（避免回复给自己）。
   *
   * @param email - 要回复全部的原始邮件对象
   */
  private startReplyAll(email: Email) {
    this.replyingTo = email;
    this.isReplyAll = true;
    this.selectedAccount = email.account_id || null;
    const senderEmail = email.send_from?.email || "";
    // 收件人 = 原发件人 + 原收件人列表（去重）
    const toEmails = (email.send_to || []).map((a) => a.email).filter(Boolean);
    const ccEmails = (email.cc || []).map((a) => a.email).filter(Boolean);
    this.toRecipients = [...new Set(senderEmail ? [senderEmail, ...toEmails] : toEmails)];
    // 抄送 = 原抄送人列表（去重）
    this.ccRecipients = [...new Set(ccEmails)];
    // 仅在有抄送人时展开抄送区域
    this.showCc = this.ccRecipients.length > 0;
    this.toInput = "";
    this.ccInput = "";
    this.replySubject = `Re: ${email.subject || ""}`;
    this.uploadingAttachments = [];
    this.attachedFiles = [];
    this.editingSubject = false;
    // 移除自己的邮箱地址，避免回复给自己
    this.removeSelfFromRecipients(this.selectedAccount || 0);
    this.fetchMatches();
    requestAnimationFrame(() => {
      const vp = this.shadowRoot!.querySelector("#viewer-panel");
      (vp?.querySelector("crm-rich-editor") as any)?.focusEditor();
    });
  }

  /**
   * 开始内联转发邮件（在详情面板中直接编辑转发内容）。
   * 异步获取原邮件的附件列表，设置转发主题为 "Fwd:" 前缀，
   * 并预留空白编辑区域供用户输入转发内容。
   *
   * @param email - 要转发的原始邮件对象
   */
  private async startForwardInline(email: Email) {
    this.replyingTo = email;
    this.isForward = true;
    this.isReplyAll = false;
    this.selectedAccount = null;
    this.toRecipients = [];
    this.ccRecipients = [];
    this.showCc = false;
    this.toInput = "";
    this.ccInput = "";
    this.replySubject = `Fwd: ${email.subject || ""}`;
    this.uploadingAttachments = [];
    this.editingSubject = false;

    // 转发时预留空白行供用户输入
    this.forwardContent = CrmEmailsPage.DEFAULT_EMPTY_LINES;

    // 异步获取原邮件附件列表，转发时自动携带原附件
    try {
      const attRes = await emailApi.getAttachments(email.id);
      this.attachedFiles = (attRes.data || []).map((a: any) => ({
        id: a.id, filename: a.filename, size: a.file_size, progress: 100,
      }));
    } catch {
      this.attachedFiles = [];
    }

    requestAnimationFrame(() => {
      const vp = this.shadowRoot!.querySelector("#viewer-panel");
      (vp?.querySelector("crm-rich-editor") as any)?.focusEditor();
    });
  }

  /**
   * 从收件人和抄送人列表中移除指定账户自身的邮箱地址。
   * 用于"回复全部"时避免把发送账户自己的邮箱包含在收件人中。
   *
   * @param accountId - 发送账户 ID
   */
  private removeSelfFromRecipients(accountId: number) {
    const acct = this.accounts.find((a) => a.id === accountId);
    if (!acct?.address) return;
    const addr = acct.address.toLowerCase();
    // 不区分大小写地过滤掉自己的邮箱
    this.toRecipients = this.toRecipients.filter(
      (e) => e.toLowerCase() !== addr,
    );
    this.ccRecipients = this.ccRecipients.filter(
      (e) => e.toLowerCase() !== addr,
    );
  }

  /**
   * 取消回复/转发状态，重置所有回复相关字段。
   * 清空回复对象、主题、附件等，使详情面板恢复为纯查看模式。
   */
  private cancelReply() {
    this.replyingTo = null;
    this.isReplyAll = false;
    this.isForward = false;
    this.replySubject = "";
    this.forwardContent = "";
    this.editingSubject = false;
    this.uploadingAttachments = [];
    this.attachedFiles = [];
  }

  /**
   * 加载指定账户的激活签名并插入到编辑器中。
   * 如果账户有激活签名，则调用 insertSignatureToEditor 将签名追加到编辑器末尾。
   *
   * @param accountId - 邮件账户 ID
   */
  private async loadActiveSignature(accountId: number | null) {
    const requestId = ++this._signatureRequestId;
    if (!accountId) return;
    const sig = await emailStore.fetchActiveSignature(accountId);
    if (requestId === this._signatureRequestId && accountId === this.selectedAccount && sig?.content) {
      const editor = this.shadowRoot!.querySelector("crm-rich-editor") as any;
      this.insertSignatureToEditor(editor, accountId, sig.id, sig.content);
    }
  }

  /**
   * 提交回复/转发邮件。
   * 该方法是内联回复/转发的核心发送逻辑：
   * 1. 验证收件人和发送账户是否已选择。
   * 2. 从编辑器提取内容（去除签名）并获取内联图片 ID。
   * 3. 根据是回复还是转发，构建完整的邮件内容：
   *    - 回复：在用户内容后追加引用块（quoteCitation + 原邮件 HTML）
   *    - 转发：在用户内容后追加转发头信息 + 原邮件 HTML
   * 4. 构建 RFC 2822 邮件引用链（references + in_reply_to + thread_id）。
   * 5. 调用 emailSendApi.send 发送邮件。
   * 6. 发送成功后取消回复状态、关闭详情面板、刷新邮件列表。
   */
  private async handleReplySubmit() {
    // 验证：至少一个收件人
    if (this.toRecipients.length === 0) {
      toast.error("请添加至少一个收件人");
      return;
    }
    // 验证：已选择发送账户
    if (!this.selectedAccount) {
      toast.error("请选择发送账户");
      return;
    }

    const viewerPanel = this.shadowRoot!.querySelector("#viewer-panel");
    const editor = viewerPanel?.querySelector("crm-rich-editor") as any;
    // 提取编辑器内容（去除签名部分）
    const content = this.editorContentWithoutSignature(editor);
    // 获取内联图片的 ID 列表（用于服务端关联图片）
    const inlineImageIds = editor?.exportInlineImageIds
      ? editor.exportInlineImageIds()
      : [];

    const refs = this.replyingTo!;
    // 构建邮件引用链：原始 references + 当前 message_id
    const references = refs.references
      ? `${refs.references} ${refs.message_id}`
      : refs.message_id || "";
    let fullContent: string;
    if (this.isForward) {
      // 转发内容 = 用户输入 + 转发头 + 原邮件 HTML/纯文本（清理后的）
      fullContent = content + buildForwardHeaderHtml(refs) + buildQuoteBodyHtml(refs);
    } else {
      // 回复模式：在用户内容后追加引用块（引用头 + 原邮件 HTML/纯文本）
      fullContent = content +
        `<blockquote style="border-left:3px solid #e0e1e6;margin:0;padding-left:12px;color:#60646c;">` +
        `<p>${buildQuoteCitationHtml(refs)}</p><br>${buildQuoteBodyHtml(refs)}</blockquote>`;
    }

    try {
      await emailSendApi.send({
        account_id: this.selectedAccount,
        to_email: this.toRecipients,
        cc: this.ccRecipients,
        subject: this.replySubject,
        content: fullContent,
        content_type: "html",
        attachment_ids: this.attachedFiles.map((f) => f.id),
        inline_image_ids: inlineImageIds,
        in_reply_to: refs.message_id || "",
        references,
        thread_id: refs.thread_id || refs.message_id || "",
      });
      toast.info("邮件已加入发件箱");
      // 发送成功后取消回复状态、关闭详情面板、刷新列表
      this.cancelReply();
      this.closeViewer();
      this.loadEmails();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "发送失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 保存回复/转发邮件为草稿。
   * 提取编辑器内容（去除签名），构建引用链，
   * 调用 emailSendApi.saveDraft 保存草稿。保存成功后关闭回复面板。
   */
  private async handleReplySaveDraft() {
    const viewerPanel = this.shadowRoot!.querySelector("#viewer-panel");
    const editor = viewerPanel?.querySelector("crm-rich-editor") as any;
    const content = this.editorContentWithoutSignature(editor);

    const refs = this.replyingTo!;
    // 构建邮件引用链
    const references = refs.references
      ? `${refs.references} ${refs.message_id}`
      : refs.message_id || "";

    try {
      await emailSendApi.saveDraft({
        account_id: this.selectedAccount || 0,
        to_email: this.toRecipients,
        cc: this.ccRecipients,
        subject: this.replySubject,
        content,
        content_type: "html",
        customer_id: refs.customer_id || 0,
        attachment_ids: this.attachedFiles.map((f) => f.id),
        inline_image_ids: [],
        in_reply_to: refs.message_id || "",
        references,
        thread_id: refs.thread_id || refs.message_id || "",
      });
      toast.success("草稿已保存");
      this.cancelReply();
      this.closeViewer();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "保存失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 附件上传开始事件处理。
   * 将新附件添加到 uploadingAttachments 列表中，显示上传进度。
   *
   * @param e - CustomEvent，detail 包含 name（文件名）和 size（文件大小）
   */
  private onAttachmentStart = (e: Event) => {
    const { name, size } = (e as CustomEvent).detail;
    this.uploadingAttachments = [
      ...this.uploadingAttachments,
      { name, size, progress: 0 },
    ];
    this.requestUpdate();
  };

  /**
   * 附件上传进度更新事件处理。
   * 根据文件名匹配并更新对应附件的上传进度百分比。
   *
   * @param e - CustomEvent，detail 包含 name（文件名）和 progress（进度 0-100）
   */
  private onAttachmentProgress = (e: Event) => {
    const { name, progress } = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.map((a) =>
      a.name === name ? { ...a, progress } : a,
    );
  };

  /**
   * 附件上传完成事件处理。
   * 将上传中的附件从 uploadingAttachments 移除，添加到 attachedFiles 列表中。
   *
   * @param e - CustomEvent，detail 包含 id（附件 ID）、name（文件名）、size（文件大小）
   */
  private onAttachmentComplete = (e: Event) => {
    const { id, name, size } = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.filter(
      (a) => a.name !== name,
    );
    this.attachedFiles = [...this.attachedFiles, { id, filename: name, size }];
    this.requestUpdate();
  };

  /**
   * 附件上传错误事件处理。
   * 将上传失败的附件从 uploadingAttachments 列表中移除。
   *
   * @param e - CustomEvent，detail 包含 name（文件名）
   */
  private onAttachmentError = (e: Event) => {
    const { name } = (e as CustomEvent).detail;
    this.uploadingAttachments = this.uploadingAttachments.filter(
      (a) => a.name !== name,
    );
    this.requestUpdate();
  };

  /**
   * 移除已上传的附件。
   * 通过索引从 attachedFiles 列表中移除指定附件。
   *
   * @param index - 要移除的附件在列表中的索引
   */
  private removeAttachment(index: number) {
    this.attachedFiles = removeAttachmentByIndex(this.attachedFiles, index);
  }

  // ========== 收件人/抄送搜索 ==========

  /**
   * 搜索收件人或抄送人（带 300ms 防抖）。
   * 当输入长度小于 3 个字符时清空下拉选项。
   * 搜索结果通过 `searchRecipientOptions` 工具函数获取（搜索客户邮箱）。
   *
   * @param target - "to" 表示搜索收件人，"cc" 表示搜索抄送人
   */
  private searchRecipients(target: "to" | "cc") {
    const input = target === "to" ? this.toInput : this.ccInput;

    // 清除对应目标的防抖定时器
    const clearTimer = (t: "to" | "cc") => {
      if (t === "to" && this.toTimer) {
        clearTimeout(this.toTimer);
        this.toTimer = null;
      } else if (t === "cc" && this.ccTimer) {
        clearTimeout(this.ccTimer);
        this.ccTimer = null;
      }
    };
    clearTimer(target);

    // 输入少于 3 个字符时不搜索，直接清空下拉
    const q = input.trim();
    if (q.length < 3) {
      this.clearDropdown(target);
      return;
    }

    // 设置 300ms 防抖定时器，避免每次输入都发起 API 请求
    const setTimer = (t: "to" | "cc", value: number | null) => {
      if (t === "to") this.toTimer = value;
      else this.ccTimer = value;
    };
    setTimer(target, window.setTimeout(async () => {
      setTimer(target, null);
      try {
        const options = await searchRecipientOptions(q);
        if (target === "to") {
          this.toOptions = options;
          this.showToDropdown = options.length > 0;
        } else {
          this.ccOptions = options;
          this.showCcDropdown = options.length > 0;
        }
      } catch {
        this.clearDropdown(target);
      }
    }, 300));
  }

  /**
   * 清空指定目标的搜索下拉选项。
   *
   * @param target - "to" 清空收件人下拉，"cc" 清空抄送人下拉
   */
  private clearDropdown(target: "to" | "cc") {
    if (target === "to") {
      this.toOptions = [];
      this.showToDropdown = false;
    } else {
      this.ccOptions = [];
      this.showCcDropdown = false;
    }
  }

  /**
   * 添加收件人或抄送人到列表。
   * 如果邮箱已存在则不重复添加。添加后清空输入框和下拉选项，并刷新客户匹配。
   *
   * @param target - "to" 添加到收件人列表，"cc" 添加到抄送人列表
   * @param email - 要添加的邮箱地址
   */
  private addRecipient(target: "to" | "cc", email: string) {
    const recipients = target === "to" ? this.toRecipients : this.ccRecipients;
    // 避免重复添加
    if (recipients.includes(email)) return;
    if (target === "to") {
      this.toRecipients = [...this.toRecipients, email];
      this.toInput = "";
      this.toOptions = [];
      this.showToDropdown = false;
    } else {
      this.ccRecipients = [...this.ccRecipients, email];
      this.ccInput = "";
      this.ccOptions = [];
      this.showCcDropdown = false;
    }
    // 添加/删除收件人后刷新客户匹配信息
    this.fetchMatches();
  }

  /**
   * 从列表中移除指定索引的收件人或抄送人。
   *
   * @param target - "to" 从收件人列表移除，"cc" 从抄送人列表移除
   * @param index - 要移除的收件人索引
   */
  private removeRecipient(target: "to" | "cc", index: number) {
    if (target === "to")
      this.toRecipients = this.toRecipients.filter((_, i) => i !== index);
    else this.ccRecipients = this.ccRecipients.filter((_, i) => i !== index);
    this.fetchMatches();
  }

  /**
   * 处理收件人/抄送人输入框的键盘事件。
   * - Backspace：输入框为空时删除最后一个 chip（收件人）
   * - Enter：验证邮箱格式后添加为新收件人
   *
   * @param target - "to" 或 "cc"
   * @param e - 键盘事件对象
   */
  private handleChipKeydown(target: "to" | "cc", e: KeyboardEvent) {
    const input = target === "to" ? this.toInput : this.ccInput;
    const recipients = target === "to" ? this.toRecipients : this.ccRecipients;
    // Backspace 且输入框为空时，删除最后一个 chip
    if (e.key === "Backspace" && !input && recipients.length > 0) {
      this.removeRecipient(target, recipients.length - 1);
    } else if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      const trimmed = input.trim();
      // 简单邮箱格式验证：xxx@xxx.xxx
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed))
        this.addRecipient(target, trimmed);
    }
  }

  // ========== 渲染 ==========

  /**
   * 异步获取收件人/抄送人对应的客户匹配信息。
   * 查询所有收件人和抄送人邮箱是否对应 CRM 系统中的客户，
   * 结果存入 matchedMap 以便在 UI 中展示匹配状态和点击跳转客户详情。
   */
  private async fetchMatches() {
    this.matchedMap = await fetchRecipientMatches(this.toRecipients, this.ccRecipients);
  }

  /**
   * 处理收件人 chip 的点击事件。
   * 如果该邮箱匹配到客户，则打开客户详情弹窗。
   *
   * @param email - 被点击的邮箱地址
   */
  private handleChipClick(email: string) {
    handleRecipientChipClick(email, this.matchedMap, this.shadowRoot);
  }

  /**
   * 渲染收件人/抄送人的 chip 输入组件。
   * 该方法生成一个可输入邮箱地址的容器，已添加的收件人显示为可删除的 chip 标签，
   * 同时支持通过下拉列表选择搜索到的客户邮箱。
   *
   * @param target - "to" 渲染收件人输入，"cc" 渲染抄送人输入
   * @param borderless - 是否使用无边框样式（内联回复时为 true）
   * @returns Lit html 模板
   */
  private renderChipInput(target: "to" | "cc", borderless = false) {
    const recipients = target === "to" ? this.toRecipients : this.ccRecipients;
    const input = target === "to" ? this.toInput : this.ccInput;
    const options = target === "to" ? this.toOptions : this.ccOptions;
    const showDropdown =
      target === "to" ? this.showToDropdown : this.showCcDropdown;

    // borderless 模式用于内联回复区域，使用透明边框
    const borderClass = borderless
      ? "border-transparent focus-within:border-primary"
      : "border-border";

    const bgClass = borderless ? "" : "bg-surface";

    return html`
      <div
        class="flex flex-wrap items-center gap-1.5 px-2 py-1.5 border ${borderClass} ${bgClass} rounded-md text-sm transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 min-h-[42px] relative"
        @click=${() => {
          // 点击容器区域时聚焦输入框
          const inp = this.shadowRoot!.querySelector(
            `.chip-input-${target}`,
          ) as HTMLInputElement;
          inp?.focus();
        }}
      >
        ${recipients.map(
          (email, i) => {
            // 检查该邮箱是否匹配到 CRM 客户
            const isMatched = this.matchedMap.has(email.toLowerCase());
            return html`
            <span
              class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium shrink-0 ${isMatched ? 'text-info border border-info/40 bg-info/5 cursor-pointer hover:underline' : 'text-text-muted border border-border bg-bg'}"
              @click=${() => { if (isMatched) this.handleChipClick(email); }}
            >
              ${email}
              <button
                type="button"
                class="ml-0.5 rounded-full cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center w-3.5 h-3.5 text-primary/60 hover:text-primary"
                @click=${(e: Event) => {
                  // 阻止事件冒泡，避免触发 chip 本身的点击
                  e.stopPropagation();
                  this.removeRecipient(target, i);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  class="w-2.5 h-2.5"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </span>
          `;
          },
        )}
        <input
          type="text"
          class="chip-input-${target} flex-1 min-w-[120px] px-1 py-1 border-none outline-none bg-transparent text-sm text-text placeholder:text-text-muted"
          placeholder="${recipients.length > 0
            ? "添加更多..."
            : "输入邮箱或搜索客户..."}"
          .value=${input}
          @input=${(e: Event) => {
            if (target === "to") {
              this.toInput = (e.target as HTMLInputElement).value;
            } else {
              this.ccInput = (e.target as HTMLInputElement).value;
            }
            this.searchRecipients(target);
          }}
          @keydown=${(e: KeyboardEvent) => this.handleChipKeydown(target, e)}
          @focus=${() => {
            if (options.length > 0) {
              if (target === "to") this.showToDropdown = true;
              else this.showCcDropdown = true;
            }
          }}
          @blur=${() => setTimeout(() => this.clearDropdown(target), 200)}
        />
        ${showDropdown
          ? html`
              <div
                class="absolute left-0 right-0 top-full mt-1 bg-surface border border-border rounded-md shadow-xl overflow-hidden"
                style="z-index:9999"
              >
                <ul class="max-h-48 overflow-y-auto py-1">
                  ${options.map(
                    (opt) => html`
                      <li
                        class="px-3 py-2 text-sm cursor-pointer transition-colors text-text hover:bg-bg"
                        @click=${() => this.addRecipient(target, opt.value)}
                        @mousedown=${(e: Event) => e.preventDefault()}
                      >
                        ${opt.label}
                      </li>
                    `,
                  )}
                  ${options.length === 0
                    ? html`<li
                        class="px-3 py-4 text-sm text-text-muted text-center"
                      >
                        未找到匹配客户
                      </li>`
                    : ""}
                </ul>
              </div>
            `
          : ""}
      </div>
    `;
  }

  /**
   * 根据当前文件夹和邮件状态构建操作菜单项列表。
   * 不同文件夹下可用的操作不同：
   * - 发件箱：仅支持删除和重试
   * - 收件箱：支持标记已读/未读、归档、添加提醒、删除
   * - 已发送：支持标记已读/未读、添加提醒、删除
   * - 垃圾箱：支持恢复、添加提醒、删除
   * - 其他文件夹：支持添加提醒、删除
   *
   * @param email - 当前邮件对象
   * @returns MenuItem 数组，包含每项的 label、icon、action 等
   */
  private getActionMenuItems(email: Email): MenuItem[] {
    const folder = this.currentFolder;
    const items: MenuItem[] = [];

    // 发件箱不提供已读/归档等操作
    if (folder !== "outbox") {
      // 已发送文件夹不提供已读/未读切换
      if (folder !== "sent") {
        items.push({
          label: email.is_read === 0 ? "标为已读" : "标为未读",
          icon: email.is_read === 0
            ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
            : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`,
          action: () => this.handleMarkAsRead(email),
        });
      }
      // 仅收件箱提供归档操作
      if (folder === "inbox") {
        items.push({
          label: "归档",
          icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>`,
          action: () => this.handleArchive(email),
        });
      }
      // 垃圾箱提供恢复操作
      if (folder === "trash") {
        items.push({
          label: "恢复",
          icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>`,
          action: () => this.handleRestore(email),
        });
      }
      // 添加跟进提醒（需要邮件关联了客户才可用）
      items.push({
        label: "添加提醒",
        icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
        action: () => this.handleSetReminder(email),
        // 未关联客户的邮件不可添加提醒
        disabled: !email.customer_id,
        disabledHint: email.customer_id ? undefined : "需关联客户",
      });
    }

    // 所有文件夹都支持删除操作
    items.push({
      label: "删除",
      icon: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`,
      action: () => this.handleDelete(email),
      danger: true,
    });

    return items;
  }

  /**
   * 转发邮件（独立窗口模式）。
   * 关闭详情面板后打开 compose-panel 进行转发。
   * 异步获取原邮件详情和附件，构建转发头信息（日期/发件人/收件人/抄送/主题）
   * 和转发正文 HTML，传递给 compose-panel 展示。
   *
   * @param email - 要转发的邮件对象
   */
  private async handleForward(email: Email) {
    // 关闭详情面板，避免 z-index 遮挡
    this.closeViewer();

    this.replySubject = `Fwd: ${email.subject || ""}`;
    this.replyingTo = null;
    // 重置草稿状态
    this.composeDraftId = 0;
    this.composeDraftToEmail = "";
    this.composeDraftContent = CrmEmailsPage.DEFAULT_EMPTY_LINES;
    this.composeDraftTo = [];
    this.composeDraftCc = [];
    this.composeDraftAccountId = 0;
    this.composeDraftAttachments = [];
    this.composeForwardHeader = "";
    this.composeForwardHtml = "";
    // 递增会话计数器，通知 compose-panel 开始新的转发会话
    const composeSession = ++this._composeSession;

    // 打开 compose-panel
    const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
    panel.show("转发邮件", 1080, "bottom");

    try {
      // 获取原邮件完整详情
      const detail = await emailApi.get(email.id);
      if (composeSession !== this._composeSession) return;
      this.composeForwardHeader = buildForwardHeaderHtml(detail);
      this.composeForwardHtml = buildQuoteBodyHtml(detail);

      // 获取原邮件附件列表，转发时自动携带
      try {
        const attRes = await emailApi.getAttachments(email.id);
        if (composeSession !== this._composeSession) return;
        this.composeDraftAttachments = (attRes.data || []).map((a: any) => ({
          id: a.id, filename: a.filename, size: a.file_size,
        }));
      } catch {
        if (composeSession === this._composeSession) this.composeDraftAttachments = [];
      }
    } catch {
      if (composeSession === this._composeSession) {
        // 获取详情失败时清空转发头和内容
        this.composeForwardHeader = "";
        this.composeForwardHtml = "";
      }
    }
  }

  /**
   * 标记邮件为已读或未读（切换状态）。
   *
   * @param email - 要切换已读状态的邮件
   */
  private async handleMarkAsRead(email: Email) {
    try {
      await emailStore.markAsRead(email.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "操作失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 切换邮件星标状态。
   * 调用 API 切换星标后，本地更新邮件列表中的星标状态。
   *
   * @param email - 要切换星标的邮件
   */
  private async handleToggleStar(email: Email) {
    try {
      const res = await emailApi.toggleStar(email.id);
      // 更新本地邮件列表中的星标状态
      this.emails = this.emails.map(e =>
        e.id === email.id ? { ...e, is_starred: res.is_starred } : e
      );
      // 同步到 store
      emailStore.updateEmailStarred(email.id, res.is_starred);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "操作失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 归档邮件（从收件箱移到归档文件夹）。
   * 需要用户确认后执行。
   *
   * @param email - 要归档的邮件
   */
  private async handleArchive(email: Email) {
    if (!(await showConfirm("确定归档这封邮件吗？"))) return;
    try {
      await emailSendApi.archive(email.id);
      toast.info("已归档");
      this.loadEmails();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "归档失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 重新发送发件箱中失败或发送中的邮件。
   * 需要用户确认后执行。
   *
   * @param email - 要重试发送的邮件
   */
  private async handleRetry(email: Email) {
    if (!(await showConfirm("确定要重新发送这封邮件吗？"))) return;
    try {
      await emailSendApi.retry(email.id);
      toast.info("已重新加入发送队列");
      this.loadEmails();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "重试失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 为邮件添加跟进提醒。
   * 创建一个 3 天后的跟进提醒任务，关联到邮件对应的客户。
   *
   * @param email - 要添加提醒的邮件（需已关联 customer_id）
   */
  private async handleSetReminder(email: Email) {
    const customerId = email.customer_id || 0;
    // 计算提醒时间：当前时间 + 3 天（86400 秒/天）
    const future = Math.floor(Date.now() / 1000) + 3 * 86400;
    try {
      await followUpApi.create({ customer_id: customerId, email_id: email.id, remind_at: future, note: email.subject || '' });
      toast.success('已添加3天后跟进提醒');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '操作失败';
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 删除邮件（软删除，移到垃圾箱）。
   * 需要用户确认。删除后如果当前详情面板正在查看该邮件，
   * 则自动导航到列表中的下一封邮件（优先未读邮件）。
   *
   * @param email - 要删除的邮件
   */
  private async handleDelete(email: Email) {
    if (!(await showConfirm("确定要删除这封邮件吗？"))) return;
    try {
      await emailStore.deleteEmail(email.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "删除失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
    // 如果当前正在查看被删除的邮件，自动导航到下一封
    if (this.selectedEmail && email.id === this.selectedEmail.id) {
      // 等待 DOM 更新完成后再查找下一封邮件
      await this.updateComplete;
      // 排序：未读邮件优先显示
      const sorted = [...this.emails].sort((a, b) => (a.is_read === 0 ? -1 : 1) - (b.is_read === 0 ? -1 : 1));
      const nextEmail = sorted[0];
      if (nextEmail && nextEmail.id !== email.id) {
        this.viewEmail(nextEmail);
      } else {
        this.closeViewer();
      }
    }
  }
  /**
   * 从 URL query 参数深链打开邮件详情。
   * 读取 `email_id` 参数（如 /crm/emails?email_id=123，来自首页日历跳转），
   * 获取邮件完整详情后调用 viewEmail 打开；无参数或获取失败时静默忽略。
   * 需等待首次渲染完成（updateComplete）确保 viewer-panel 已挂载到 DOM。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async openEmailFromQuery(): Promise<void> {
    const emailId = new URLSearchParams(window.location.search).get("email_id");
    if (!emailId) return;
    const viewerRequestId = this._viewerRequestId;
    await this.updateComplete;
    try {
      const detail = await emailApi.get(Number(emailId));
      if (viewerRequestId !== this._viewerRequestId) return;
      this.viewEmail(detail);
    } catch {
      // 邮件不存在或无权限时静默忽略，页面保持列表态
    }
  }

  /**
   * 查看邮件详情或编辑草稿。
   * 该方法根据邮件所在文件夹决定行为：
   * - 草稿箱：打开 compose-panel 编辑草稿，并尝试从线程中找回原邮件引用
   * - 其他文件夹：打开 viewer-panel 显示邮件详情，自动标记为已读
   *
   * @param email - 要查看的邮件对象
   */
  private async viewEmail(email: Email) {
    // 草稿邮件：打开 compose-panel 进行编辑
    if (email.folder === "drafts") {
      const composeSession = ++this._composeSession;
      this.composeForwardHeader = "";
      this.composeForwardHtml = "";
      const panel = this.shadowRoot!.querySelector("#compose-panel") as any;
      panel.show("编辑草稿", 1080, "bottom");
      try {
        // 获取草稿完整详情
        const detail = await emailApi.get(email.id);
        if (composeSession !== this._composeSession) return;
        this.replySubject = detail.subject || "";
        this.composeDraftToEmail = detail.send_to?.[0]?.email || "";
        this.composeDraftId = detail.id;
        this.composeDraftContent = detail.html_content || detail.content || "";
        this.composeDraftTo = (detail.send_to || []).map((r: any) => r?.email || "").filter(Boolean);
        this.composeDraftCc = (detail.cc || []).map((r: any) => r?.email || "").filter(Boolean);
        this.composeDraftAccountId = detail.account_id || 0;
        // 获取草稿附件列表
        try {
          const attRes = await emailApi.getAttachments(email.id);
          if (composeSession !== this._composeSession) return;
          this.composeDraftAttachments = (attRes.data || []).map((a: any) => ({
            id: a.id, filename: a.filename, size: a.file_size,
          }));
        } catch {
          if (composeSession === this._composeSession) this.composeDraftAttachments = [];
        }
        // 回复/转发草稿：从线程中找回原邮件，重新展示引用
        if (detail.in_reply_to) {
          try {
            // 通过线程接口查找原始邮件
            const threadRes = await emailThreadApi.getEmailThread(email.id);
            if (composeSession !== this._composeSession) return;
            const original = (threadRes.messages || []).find(
              (m: Email) => m.message_id === detail.in_reply_to
            );
            if (original) {
              // 如果线程中的邮件缺少 html_content，再请求完整详情
              const origDetail = original.html_content ? original : await emailApi.get(original.id);
              if (composeSession !== this._composeSession) return;
              // 判断是转发草稿还是回复草稿
              const isFwd = (detail.subject || "").startsWith("Fwd:");
              if (isFwd) {
                // 转发草稿：构建转发头
                this.composeForwardHeader = buildForwardHeaderHtml(origDetail);
                this.composeForwardHtml = buildQuoteBodyHtml(origDetail);
              } else {
                // 回复草稿：构建引用头
                this.composeForwardHeader = buildQuoteHeaderHtml(origDetail);
                this.composeForwardHtml = buildQuoteBodyHtml(origDetail);
              }
            }
          } catch {
            // 线程获取失败，不展示引用
          }
        }
      } catch {
        if (composeSession !== this._composeSession) return;
        // 获取草稿详情失败时，使用列表中的基本信息
        this.replySubject = email.subject || "";
        this.composeDraftToEmail = email.send_to?.[0]?.email || "";
        this.composeDraftId = email.id;
        this.composeDraftContent = "";
        this.composeDraftTo = [];
        this.composeDraftCc = [];
        this.composeDraftAccountId = 0;
        this.composeDraftAttachments = [];
      }
      return;
    }
    // 非草稿邮件：打开 viewer-panel 显示详情
    const viewerRequestId = ++this._viewerRequestId;
    this.isViewingEmail = true;
    const panel = this.shadowRoot!.querySelector("#viewer-panel") as any;
    panel.show(email.subject || "邮件详情", 1080, "bottom");
    try {
      // 获取邮件完整详情（含正文、附件等）
      const detail = await emailApi.get(email.id);
      if (viewerRequestId !== this._viewerRequestId) return;
      this.selectedEmail = detail;
    } catch {
      // 获取详情失败时回退到列表中的基本邮件对象
      if (viewerRequestId === this._viewerRequestId) this.selectedEmail = email;
    } finally {
      if (viewerRequestId === this._viewerRequestId) this.isViewingEmail = false;
    }
    if (viewerRequestId !== this._viewerRequestId) return;
    // 如果邮件未读，自动标记为已读
    if (email.is_read === 0) {
      try {
        await emailStore.markAsRead(email.id);
      } catch {
        /* silent */
      }
    }
  }

  /**
   * 处理从 email-list-panel 子组件触发的邮件选择事件。
   * 如果当前正在回复状态，先取消回复，再打开新邮件的详情。
   *
   * @param e - CustomEvent，detail.email 为选中的邮件对象
   */
  private onPanelEmailSelect = (e: Event) => {
    const email = (e as CustomEvent).detail.email as Email;
    if (email) {
      // 切换邮件时取消当前回复状态
      if (this.replyingTo) this.cancelReply();
      this.viewEmail(email);
    }
  };

  /**
   * 处理从 email-viewer 子组件触发的邮件删除事件。
   * 删除后自动导航到列表中的下一封邮件（优先未读邮件）。
   *
   * @param e - CustomEvent，detail.emailId 为被删除的邮件 ID
   */
  private onViewerEmailDeleted = async (e: CustomEvent) => {
    const deletedId = e.detail.emailId;
    // 等待 DOM 更新完成后再查找下一封邮件
    await this.updateComplete;
    // 排序：未读邮件优先显示
    const sorted = [...this.emails].sort((a, b) => (a.is_read === 0 ? -1 : 1) - (b.is_read === 0 ? -1 : 1));
    const nextEmail = sorted[0];
    if (nextEmail && nextEmail.id !== deletedId) {
      this.viewEmail(nextEmail);
    } else {
      this.closeViewer();
    }
  };

  /** 关闭详情面板（viewer-panel），清除选中邮件和回复状态 */
  private closeViewer() {
    this._viewerRequestId++;
    this.isViewingEmail = false;
    this.selectedEmail = null;
    this.cancelReply();
    const panel = this.shadowRoot!.querySelector("#viewer-panel") as any;
    panel.close();
  }

  /**
   * 切换邮件文件夹。
   * 重置过滤器、页码和选中状态，清空当前邮件列表，
   * 然后从 store 加载新文件夹的邮件数据。
   *
   * @param folder - 目标文件夹名称（inbox/outbox/sent/drafts/trash/archive）
   */
  private switchFolder(folder: string) {
    this.clearSelectedTask();
    this.currentFolder = folder;
    this.clearSelection();
    this.currentPage = 1;
    // 收件箱默认显示未读邮件，其他文件夹显示全部
    this.filterRead = folder === "inbox" ? 0 : null;
    this.filterTagId = null;
    // 清空当前列表，避免切换文件夹时显示旧数据
    this.emails = [];
    this.total = 0;
    emailStore.setCurrentFolder(folder);
    emailStore.clearList();
    // 加载新文件夹的邮件
    emailStore.fetchEmails({
      page: 1,
      per_page: this.perPage,
      folder,
      is_read: this.filterRead === null ? undefined : this.filterRead,
    });
    // 刷新文件夹计数（更新未读数等）
    emailStore.fetchFolderCounts();
  }

  /**
   * 按标签筛选邮件。
   * 点击同一标签可切换取消筛选。标签筛选优先于已读筛选。
   *
   * @param tagId - 要筛选的标签 ID
   */
  private filterByTag(tagId: number) {
    this.clearSelectedTask();
    // 点击当前已选中的标签则取消筛选
    const newTagId = this.filterTagId === tagId ? null : tagId;
    this.filterTagId = newTagId;
    this.clearSelection();
    this.currentPage = 1;
    emailStore.setCurrentTagId(newTagId);
    emailStore.fetchEmails({
      page: 1,
      per_page: this.perPage,
      folder: this.currentFolder,
      // 有标签筛选时不传递已读过滤
      is_read: newTagId === null && this.filterRead !== null ? this.filterRead : undefined,
      tag_id: newTagId ?? undefined,
    });
  }

  /**
   * 从垃圾箱恢复邮件到收件箱。
   *
   * @param email - 要恢复的邮件
   */
  private async handleRestore(email: Email) {
    try {
      await emailStore.restoreEmail(email.id);
      toast.info("已恢复到收件箱");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "恢复失败";
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 渲染文件夹侧边栏中的单个文件夹项。
   * 显示文件夹名称、未读数（徽章）或总数。
   *
   * @param folder - 文件夹标识
   * @param label - 文件夹显示名称
   * @param counts - 各文件夹的计数数据
   * @returns Lit html 模板
   */
  private renderFolderItem(
    folder: string,
    label: string,
    counts: Array<{ folder: string; total: number; unread: number }>,
  ) {
    // 从计数数组中查找当前文件夹的计数
    const info = counts.find((c) => c.folder === folder) || {
      total: 0,
      unread: 0,
    };
    const isActive = this.currentFolder === folder;
    return html` <div
      class="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors text-sm
                  ${isActive
        ? "bg-primary/10 text-primary font-semibold"
        : "text-text-secondary hover:bg-surface"}"
      @click=${() => this.switchFolder(folder)}
    >
      <span>${label}</span>
      ${folder !== "trash" && info.unread > 0
        ? html`<span
            class="bg-primary text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center"
            >${info.unread}</span
          >`
        : folder !== "trash" && info.total > 0
          ? html`<span class="text-xs text-text-muted">${info.total}</span>`
          : ""}
    </div>`;
  }

  /** 渲染发送记录正文弹窗，以只读富文本方式展示邮件 HTML 内容。 */
  private renderTaskRecordModal() {
    const record = this.selectedTaskRecord;
    if (!record) return html``;
    const status = ["待发送", "发送中", "已发送", "发送失败"][record.send_status] || "未知";
    return html`
      <div
        class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        @click=${() => (this.selectedTaskRecord = null)}
      >
        <div
          class="bg-surface rounded-md shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden border border-border flex flex-col"
          @click=${(event: Event) => event.stopPropagation()}
        >
          <div class="flex items-center justify-between gap-4 px-6 py-4 border-b border-border flex-shrink-0">
            <div class="min-w-0">
              <h2 class="m-0 text-lg font-semibold text-text truncate" title=${record.subject}>
                ${record.subject || "无主题"}
              </h2>
              <div class="text-xs text-text-muted mt-1">收件人：${record.send_to} · 状态：${status}</div>
            </div>
            <button
              class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors flex-shrink-0"
              aria-label="关闭邮件正文"
              @click=${() => (this.selectedTaskRecord = null)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <div class="overflow-auto p-6 text-sm text-text leading-relaxed">
            ${record.content
              ? unsafeHTML(sanitizeEmailHtml(record.content))
              : html`<span class="text-text-muted">暂无正文内容</span>`}
          </div>
        </div>
      </div>
    `;
  }

  /** 渲染邮件任务详情，展示任务进度和分页发送记录。 */
  private renderTaskDetail() {
    const task = this.selectedTask;
    if (!task) return html``;
    const progress = task.total_customers > 0
      ? Math.min(100, Math.round((task.sent_count / task.total_customers) * 100))
      : 0;
    return html`
      <div class="flex-1 min-h-0 flex flex-col bg-surface rounded-md shadow-lg border border-border overflow-hidden">
        <div class="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <button
            class="text-sm text-text-secondary hover:text-primary cursor-pointer bg-transparent border-none p-0"
            @click=${this.clearSelectedTask}
          >
            返回邮件
          </button>
          <span class="text-text-muted">|</span>
          <div class="min-w-0 flex-1">
            <h2 class="m-0 text-base font-semibold text-text truncate" title=${task.task_name}>
              ${task.task_name}
            </h2>
            <div class="text-xs text-text-muted mt-1">邮件任务 · 发送记录</div>
          </div>
          <span class="text-xs text-primary bg-primary/10 rounded-full px-2.5 py-1">进行中</span>
        </div>
        <div class="px-5 py-4 border-b border-border flex-shrink-0">
          <div class="flex items-center justify-between text-sm mb-2">
            <span class="text-text-secondary">发送进度</span>
            <span class="text-text font-medium">${task.sent_count} / ${task.total_customers}（失败 ${task.failed_count}）</span>
          </div>
          <div class="h-2 rounded-full bg-bg overflow-hidden">
            <div
              class="task-progress-bar h-full rounded-full transition-all ${task.failed_count > 0 ? "error" : "success"}"
              style="width:${progress}%"
            ></div>
          </div>
        </div>
        <div class="overflow-auto flex-1 min-h-0 relative">
          <crm-loading-overlay .active=${this.taskRecordsLoading}></crm-loading-overlay>
          <table class="w-full">
            <thead>
              <tr class="border-b border-border">
                <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary">收件人</th>
                <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary">主题</th>
                <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary">状态</th>
                <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary">发送时间</th>
              </tr>
            </thead>
            <tbody>
              ${this.taskRecordsLoading
                ? html`<tr><td colspan="4" class="text-center py-12 text-text-muted">加载中...</td></tr>`
                : this.taskRecords.length === 0
                  ? html`<tr><td colspan="4" class="text-center py-12 text-text-muted">暂无发送记录</td></tr>`
                  : this.taskRecords.map((record) => {
                    const status = ["待发送", "发送中", "已发送", "发送失败"][record.send_status] || "未知";
                    const statusClass = record.send_status === 2
                      ? "bg-success/10 text-success"
                      : record.send_status === 3
                        ? "bg-error/10 text-error"
                        : record.send_status === 1
                          ? "bg-primary/10 text-primary"
                          : "bg-bg text-text-secondary";
                    return html`<tr
                      class="border-b border-border hover:bg-bg/50 cursor-pointer ${this.selectedTaskRecord?.id === record.id ? "bg-primary/5" : ""}"
                      @click=${() => (this.selectedTaskRecord = record)}
                      title="点击查看发送正文"
                    >
                      <td class="px-4 py-2.5 text-sm text-text">${record.send_to}</td>
                      <td class="px-4 py-2.5 text-sm text-text max-w-[280px] truncate" title=${record.subject}>${record.subject}</td>
                      <td class="px-4 py-2.5"><span class="inline-flex items-center px-2 py-0.5 rounded text-xs ${statusClass}">${status}</span></td>
                      <td class="px-4 py-2.5 text-xs text-text-muted">${record.sent_time ? new Date(record.sent_time * 1000).toLocaleString("zh-CN") : "-"}</td>
                    </tr>`;
                  })}
            </tbody>
          </table>
        </div>
        <crm-pagination
          .current=${this.taskRecordsPage}
          .perPage=${20}
          .total=${this.taskRecordsTotal}
          .showTotal=${false}
          @page-change=${(event: CustomEvent) => {
            this.taskRecordsPage = event.detail.page;
            void this.loadTaskRecords();
          }}
        ></crm-pagination>
      </div>
      ${this.renderTaskRecordModal()}
    `;
  }

  /**
   * 渲染标签管理弹窗。
   * 提供标签的创建、编辑（名称和颜色）、删除功能。
   * 弹窗以全屏遮罩 + 居中对话框形式展示。
   *
   * @returns Lit html 模板（showTagManager 为 false 时返回空模板）
   */
  private renderTagManager() {
    if (!this.showTagManager) return html``;
    return html` <div
      class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50"
      @click=${() => (this.showTagManager = false)}
    >
      <div
        class="bg-surface rounded-md shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto border border-border mx-4"
        @click=${(e: Event) => e.stopPropagation()}
      >
        <div
          class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border"
        >
          <h2 class="m-0 font-semibold text-xl text-text">管理标签</h2>
          <button
            class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors"
            @click=${() => (this.showTagManager = false)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="w-5 h-5"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4">
          ${this.allTags.map(
            (tag) => html`
              <div class="flex items-center gap-3">
                ${this.editingTagId === tag.id
                  ? html`
                      <div class="flex-1 flex items-center gap-2">
                        <input
                          type="color"
                          .value=${this.editTagColor}
                          @input=${(e: Event) =>
                            (this.editTagColor = (
                              e.target as HTMLInputElement
                            ).value)}
                          class="w-8 h-8 rounded cursor-pointer border-0 p-0"
                        />
                        <input
                          type="text"
                          .value=${this.editTagName}
                          @input=${(e: Event) =>
                            (this.editTagName = (
                              e.target as HTMLInputElement
                            ).value)}
                          class="flex-1 px-3 py-1.5 border border-border rounded text-sm bg-surface"
                        />
                        <button
                          class="text-primary text-xs font-medium cursor-pointer bg-transparent border-none px-2"
                          @click=${() => {
                            emailStore.updateTag(tag.id, {
                              name: this.editTagName,
                              color: this.editTagColor,
                            });
                            this.editingTagId = null;
                          }}
                        >
                          保存
                        </button>
                        <button
                          class="text-text-muted text-xs cursor-pointer bg-transparent border-none px-2"
                          @click=${() => (this.editingTagId = null)}
                        >
                          取消
                        </button>
                      </div>
                    `
                  : html`
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                        style="background:${tag.color}20;color:${tag.color};border:1px solid ${tag.color}40"
                        >${tag.name}</span
                      >
                      <span class="flex-1"></span>
                      <button
                        class="text-text-muted hover:text-text text-xs cursor-pointer bg-transparent border-none px-1.5"
                        @click=${() => {
                          this.editingTagId = tag.id;
                          this.editTagName = tag.name;
                          this.editTagColor = tag.color;
                        }}
                      >
                        编辑
                      </button>
                      <button
                        class="text-error hover:text-error-hover text-xs cursor-pointer bg-transparent border-none px-1.5"
                        @click=${() => emailStore.deleteTag(tag.id)}
                      >
                        删除
                      </button>
                    `}
              </div>
            `,
          )}
          <div class="flex items-center gap-2 pt-2 border-t border-border">
            <input
              type="color"
              .value=${this.newTagColor}
              @input=${(e: Event) =>
                (this.newTagColor = (e.target as HTMLInputElement).value)}
              class="w-8 h-8 rounded cursor-pointer border-0 p-0"
            />
            <input
              type="text"
              placeholder="新标签名称"
              .value=${this.newTagName}
              @input=${(e: Event) =>
                (this.newTagName = (e.target as HTMLInputElement).value)}
              class="flex-1 px-3 py-1.5 border border-border rounded text-sm bg-surface"
            />
            <button
              class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium cursor-pointer disabled:opacity-50"
              ?disabled=${!this.newTagName.trim()}
              @click=${() => {
                emailStore.createTag({
                  name: this.newTagName.trim(),
                  color: this.newTagColor,
                });
                this.newTagName = "";
              }}
            >
              添加
            </button>
          </div>
        </div>
      </div>
    </div>`;
  }

  /**
   * 将 Unix 时间戳（秒）格式化为中文日期时间字符串。
   *
   * @param ts - Unix 时间戳（秒），undefined 时返回 "-"
   * @returns 格式化后的日期时间字符串
   */
  private formatDate(ts: number | undefined): string {
    return ts ? new Date(ts * 1000).toLocaleString("zh-CN") : "-";
  }

  /**
   * 截断文本到指定长度，超出部分用 "..." 表示。
   *
   * @param text - 要截断的文本
   * @param maxLength - 最大长度
   * @returns 截断后的文本，空文本返回 "-"
   */
  private truncate(text: string, maxLength: number): string {
    return text && text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text || "-";
  }

  /**
   * 格式化邮件地址显示。
   * 有名称时显示 "名称 <邮箱>"，无名称时只显示邮箱，无地址时返回 "-"。
   *
   * @param addr - 邮件地址对象（含 email 和 name 字段）
   * @returns 格式化后的地址字符串
   */
  private formatAddress(addr: EmailAddress | undefined): string {
    return addr?.email
      ? addr.name
        ? `${addr.name} <${addr.email}>`
        : addr.email
      : "-";
  }

  /**
   * 切换视图模式（列表 / 对话）。
   * - 列表模式：加载邮件列表
   * - 对话模式：加载线程列表
   *
   * @param mode - "list" 或 "thread"
   */
  private switchView(mode: "list" | "thread") {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    if (mode === "list") this.loadEmails();
    else {
      // 切换到对话模式时重置页码并加载线程列表
      this.threadPage = 1;
      emailStore.fetchThreads({ page: 1, account_id: this.filterAccountId ?? undefined });
    }
  }

  /**
   * 处理线程列表的分页页码变化。
   *
   * @param page - 新的页码
   */
  private handleThreadPageChange(page: number) {
    this.threadPage = page;
    emailStore.fetchThreads({ page, account_id: this.filterAccountId ?? undefined });
  }

  /**
   * 查看线程（对话）详情。
   * 加载线程内的所有邮件，默认展开第一封，
   * 并批量标记线程内所有未读邮件为已读（一次 API 请求）。
   *
   * @param threadId - 线程 ID
   */
  private async viewThread(threadId: string) {
    await emailStore.fetchThread(threadId);
    this.selectedThreadId = threadId;
    // 默认展开线程中的第一封邮件
    this.expandedThreadEmails = { 0: true };
    // 批量标记线程内所有未读邮件为已读（一次请求）
    const unreadIds = this.threadEmails
      .filter((e) => e.is_read === 0)
      .map((e) => e.id);
    if (unreadIds.length > 0) {
      try {
        await emailStore.markMultipleAsRead(unreadIds);
      } catch {
        /* silent */
      }
    }
  }

  /** 关闭线程详情视图，清空线程相关状态 */
  private closeThreadView() {
    this.selectedThreadId = null;
    this.threadEmails = [];
    this.expandedThreadEmails = {};
  }

  /**
   * 切换线程内某封邮件的展开/折叠状态。
   * 展开时如果邮件未读，自动标记为已读并更新线程未读状态。
   *
   * @param index - 邮件在线程数组中的索引
   */
  private async toggleThreadEmail(index: number) {
    // 记录展开前的状态：如果之前是折叠的，说明这次是展开操作
    const wasCollapsed = !this.expandedThreadEmails[index];
    this.expandedThreadEmails = {
      ...this.expandedThreadEmails,
      [index]: !this.expandedThreadEmails[index],
    };
    // 展开时检查是否需要标记为已读
    if (wasCollapsed) {
      const email = this.threadEmails[index];
      if (email && email.is_read === 0) {
        try {
          await emailStore.markAsRead(email.id);
        } catch {
          /* silent */
        }
        // 更新线程内邮件的已读状态
        this.threadEmails = this.threadEmails.map((e, i) =>
          i === index ? { ...e, is_read: 1 } : e,
        );
        // 更新线程列表中的未读标记
        if (this.selectedThreadId) {
          this.threads = this.threads.map((t) =>
            t.thread_id === this.selectedThreadId
              ? { ...t, is_unread: false }
              : t,
          );
        }
      }
    }
  }

  /**
   * 渲染发件箱邮件的发送状态徽章。
   * 状态码：0=排队中, 1=发送中, 2=已发送, 3=失败。
   *
   * @param status - 发送状态码
   * @returns Lit html 模板（带颜色的状态徽章）
   */
  private renderSendStatus(status: number | null) {
    if (status === null)
      return html`<span class="text-xs text-text-muted">-</span>`;
    const labels: Record<number, { label: string; cls: string }> = {
      0: { label: "排队中", cls: "bg-bg text-text-secondary" },
      1: { label: "发送中", cls: "bg-primary/10 text-primary" },
      2: { label: "已发送", cls: "bg-success/10 text-success" },
      3: { label: "失败", cls: "bg-error/10 text-error" },
    };
    const s = labels[status] || { label: "未知", cls: "bg-bg text-text-muted" };
    return html`<span
      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.cls}"
      >${s.label}</span
    >`;
  }

  /**
   * 渲染邮件追踪状态（已打开/未打开）。
   * 如果邮件有 opened_at 时间戳，显示绿色对勾图标；
   * 打开次数 > 1 时额外显示打开次数。
   * 未打开则显示灰色眼睛图标。
   *
   * @param email - 邮件对象
   * @returns Lit html 模板（追踪状态图标）
   */
  private renderTrackingStatus(email: Email) {
    if (email.opened_at) {
      return html`<span
        class="inline-flex items-center gap-1 text-xs text-success"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="w-3.5 h-3.5"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        ${email.open_count > 1
          ? html`<span class="text-text-muted">${email.open_count}</span>`
          : ""}
      </span>`;
    }
    return html`<span
      class="inline-flex items-center gap-1 text-xs text-text-muted"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="w-3.5 h-3.5"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    </span>`;
  }

  /**
   * 主渲染方法，构建整个邮件管理页面的 DOM 结构。
   *
   * 页面布局结构：
   * 1. 顶部工具栏：标题 + 账户筛选下拉 + 已读筛选下拉 + 列表/对话视图切换 + 批量操作按钮 + 表格工具栏
   * 2. 左侧文件夹栏：写邮件按钮 + 文件夹列表（收件箱/发件箱/已发送/草稿箱/垃圾箱/归档）+ 星标邮件 + 标签列表 + 标签管理入口
   * 3. 主内容区（根据 viewMode 切换）：
   *    - 列表模式：邮件列表表格（发件箱/收件箱/已发送有不同的列配置）+ 分页组件
   *    - 对话模式：线程列表表格 + 分页组件，或线程详情（展开/折叠的邮件列表）
   * 4. 撰写邮件面板（compose-panel，crm-side-panel）：通过 crm-email-compose-panel 子组件实现
   * 5. 快速创建客户弹窗（条件渲染）
   * 6. 邮件详情面板（viewer-panel，crm-side-panel）：含 email-list-panel 邮件列表 + email-viewer 详情/内联回复
   * 7. 标签管理弹窗
   * 8. 客户详情弹窗（crm-customer-view-modal）
   *
   * @returns Lit html 模板
   */
  render() {
    return html`
      <div class="flex flex-col h-full">
        <div
          class="flex items-center gap-3 mb-[2px] flex-shrink-0 flex-wrap"
          style="min-height:42px"
        >
          <span class="text-base font-semibold text-text">邮件管理</span>
          ${this.currentFolder !== "outbox"
            ? html`
                <div class="w-72">
                  <crm-select
                    fullWidth
                    searchable
                    .disabled=${Boolean(this.selectedTask)}
                    .options=${[
                      { label: "所有账户", value: "" },
                      ...this.accounts.filter((a) => a.status === 1).map((a) => ({
                        label: a.address,
                        value: String(a.id),
                      })),
                    ]}
                    .value=${this.filterAccountId !== null
                      ? String(this.filterAccountId)
                      : ""}
                    @change=${(e: CustomEvent) => {
                      this.filterAccountId = e.detail.value
                        ? parseInt(e.detail.value)
                        : null;
                      this.currentPage = 1; this.threadPage = 1;
                      this.clearSelection();
                      if (this.viewMode === "thread") emailStore.fetchThreads({ page: 1, account_id: this.filterAccountId ?? undefined });
                      else this.loadEmails();
                    }}
                  ></crm-select>
                </div>
                <crm-select
                  .options=${[
                    { label: "全部", value: "" },
                    { label: "未读", value: "0" },
                    { label: "已读", value: "1" },
                  ]}
                  .value=${this.filterRead !== null
                    ? String(this.filterRead)
                    : ""}
                  .disabled=${this.currentFolder !== "inbox" || Boolean(this.selectedTask)}
                  @change=${(e: CustomEvent) => {
                    this.filterRead =
                      e.detail.value === "" ? null : parseInt(e.detail.value);
                    this.currentPage = 1;
                    this.clearSelection();
                    this.loadEmails();
                  }}
                ></crm-select>
                <div class="flex bg-bg rounded-md p-0.5 border border-border">
                  <button
                    ?disabled=${Boolean(this.selectedTask)}
                    class="px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${this
                      .viewMode === "list"
                      ? "bg-surface text-text shadow-sm"
                      : "text-text-muted hover:text-text"}"
                    @click=${() => { if (!this.selectedTask) this.switchView("list"); }}
                  >
                    列表
                  </button>
                  <button
                    ?disabled=${Boolean(this.selectedTask)}
                    class="px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${this
                      .viewMode === "thread"
                      ? "bg-surface text-text shadow-sm"
                      : "text-text-muted hover:text-text"}"
                    @click=${() => { if (!this.selectedTask) this.switchView("thread"); }}
                  >
                    对话
                  </button>
                </div>
              `
            : ""}
          <div class="flex-1"></div>
          ${this.currentFolder === "inbox" && this.selectedEmailIds.size > 0
            ? html`
                <button
                  class="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded transition-colors cursor-pointer"
                  @click=${this.handleBatchMarkRead}
                >
                  标记已读 (${this.selectedEmailIds.size})
                </button>
              `
            : ""}
          <crm-table-toolbar
            .total=${this.total}
            .label=${"封邮件"}
            .loading=${this.isLoading}
            @crm-toolbar-refresh=${() => this.loadEmails()}
          ></crm-table-toolbar>
        </div>

        <div class="flex flex-1 min-h-0 gap-0">
          <!-- Folder Sidebar -->
          <div
            class="w-52 flex-shrink-0 bg-surface rounded-md mr-4 flex flex-col overflow-hidden border border-border"
          >
            <div class="p-3">
              <button
                class="w-full bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center justify-center gap-2"
                @click=${this.openPanel}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="w-4 h-4"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
                写邮件
              </button>
            </div>
            <div class="overflow-y-auto px-3 pb-3">
              <div class="border-t border-border pt-2">
                <div
                  class="text-xs font-medium text-text-muted px-3 py-1 uppercase tracking-wider"
                >
                  邮件文件夹
                </div>
                ${this.renderFolderItem("inbox", "收件箱", this.folderCounts)}
                ${this.renderFolderItem("outbox", "发件箱", this.folderCounts)}
                ${this.renderFolderItem("sent", "已发送", this.folderCounts)}
                ${this.renderFolderItem("drafts", "草稿箱", this.folderCounts)}
                ${this.renderFolderItem("trash", "垃圾箱", this.folderCounts)}
                ${this.renderFolderItem("archive", "归档", this.folderCounts)}
                <div
                  class="px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm flex items-center gap-2 mt-2"
                >
                  <span
                    class="cursor-pointer text-sm flex items-center gap-2 w-full text-text-secondary hover:text-warning transition-colors"
                    @click=${() => {
                      this.clearSelectedTask();
                      this.currentFolder = "inbox";
                      this.filterRead = null;
                      this.filterTagId = null;
                      this.currentPage = 1;
                      emailStore.setCurrentFolder("inbox");
                      emailStore.fetchEmails({
                        page: 1,
                        per_page: this.perPage,
                        folder: "inbox",
                        is_starred: 1,
                      });
                    }}
                  >
                    <span class="text-base">★</span> 星标邮件
                  </span>
                </div>
                ${this.runningTasks.length > 0
                  ? html`
                      <div class="mt-2 border-t border-border pt-2">
                        <div class="text-xs font-medium text-text-muted px-3 py-1 uppercase tracking-wider">
                          邮件任务
                        </div>
                        ${this.runningTasks.map(
                          (task) => html`
                            <div
                              class="flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${this.selectedTask?.id === task.id
                                ? "bg-primary/10 text-primary font-semibold"
                                : "text-text-secondary hover:bg-bg"}"
                              @click=${() => this.selectTask(task)}
                              title=${task.task_name}
                            >
                              <span class="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
                              <span class="truncate min-w-0">${task.task_name}</span>
                            </div>
                          `,
                        )}
                      </div>
                    `
                  : ""}
              </div>
            </div>
            ${this.allTags.length > 0
              ? html`
                  <div class="border-t border-border pt-2 pb-2 px-3">
                    <div
                      class="text-xs font-medium text-text-muted px-3 py-1 uppercase tracking-wider"
                    >
                      标签
                    </div>
                    ${this.allTags.map(
                      (tag) => html`
                        <div
                          class="flex items-center justify-between px-3 py-1.5 rounded-md cursor-pointer transition-colors text-sm
                              ${this.filterTagId === tag.id
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-text-secondary hover:bg-bg"}"
                          @click=${() => this.filterByTag(tag.id)}
                        >
                          <span class="flex items-center gap-2">
                            <span
                              class="w-2 h-2 rounded-full flex-shrink-0"
                              style="background:${tag.color}"
                            ></span>
                            <span class="truncate">${tag.name}</span>
                          </span>
                        </div>
                      `,
                    )}
                  </div>
                `
              : ""}
            <div class="border-t border-border pt-2 px-3 pb-3">
              <div
                class="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors text-sm text-text-secondary hover:bg-bg"
                @click=${() => {
                  this.showTagManager = true;
                  emailStore.fetchTags();
                }}
              >
                <span>管理标签</span>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="w-4 h-4"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </div>
            </div>
          </div>
          <!-- Main Content -->
          ${this.selectedTask ? this.renderTaskDetail() : ""}
          <div class="flex-1 min-h-0 flex flex-col ${this.selectedTask ? "hidden" : ""}">
            ${this.viewMode === "list"
              ? html` <div class="flex-1 min-h-0">
                  <div
                    class="bg-surface rounded-md shadow-lg flex flex-col h-full overflow-hidden relative"
                  >
                    <crm-loading-overlay
                      .active=${this.isLoading}
                    ></crm-loading-overlay>
                    <div class="overflow-auto flex-1 min-h-0">
                      <table class="w-full">
                        <thead>
                          <tr class="border-b border-border">
                            ${this.currentFolder === "outbox"
                              ? html`
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    状态
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    主题
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    收件人
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    发送时间
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    追踪
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                                  >
                                    操作
                                  </th>
                                `
                              : html`
                                  ${this.currentFolder === "inbox"
                                    ? html`
                                        <th class="px-4 py-2.5 text-left w-10">
                                          <label
                                            class="crm-checkbox ${this
                                              .selectedEmailIds.size > 0 &&
                                            this.selectedEmailIds.size <
                                              this.emails.length
                                              ? "indeterminate"
                                              : ""} ${this.selectedEmailIds
                                              .size === this.emails.length &&
                                            this.emails.length > 0
                                              ? "checked"
                                              : ""}"
                                          >
                                            <input
                                              type="checkbox"
                                              @change=${this.toggleSelectAll}
                                            />
                                          </label>
                                        </th>
                                      `
                                    : ""}
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider w-10"
                                  ></th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    主题
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    发件人
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    收件人
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                                  >
                                    时间
                                  </th>
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                                  >
                                    附件
                                  </th>
                                  ${this.currentFolder === "sent"
                                    ? html`<th
                                        class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                                      >
                                        追踪
                                      </th>`
                                    : ""}
                                  <th
                                    class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                                  >
                                    操作
                                  </th>
                                `}
                          </tr>
                        </thead>
                        <tbody>
                          ${this.currentFolder === "outbox"
                            ? this.emails.map(
                                (email) =>
                                  html` <tr
                                    class="hover:bg-bg/50 transition-colors border-b border-border"
                                  >
                                    <td class="px-4 py-2 text-center">
                                      <span
                                        class="cursor-pointer text-lg leading-none ${email.is_starred
                                          ? "text-yellow-400"
                                          : "text-text-muted/50 hover:text-yellow-400"}"
                                        @click=${(e: Event) => {
                                          e.stopPropagation();
                                          this.handleToggleStar(email);
                                        }}
                                        >${email.is_starred ? "★" : "☆"}</span
                                      >
                                    </td>
                                    <td
                                      class="px-4 py-2"
                                      title=${email.send_error || ""}
                                    >
                                      ${this.renderSendStatus(
                                        email.send_status,
                                      )}
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm max-w-[300px] text-text"
                                    >
                                      ${email.subject || "-"}
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm text-text-secondary"
                                    >
                                      ${email.send_to
                                        .map((a) => this.formatAddress(a))
                                        .join(", ")}
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm text-text-muted"
                                    >
                                      ${email.sent_at
                                        ? this.formatDate(email.sent_at)
                                        : "-"}
                                    </td>
                                    <td class="px-4 py-2">
                                      ${this.renderTrackingStatus(email)}
                                    </td>
                                    <td class="px-4 py-2 whitespace-nowrap">
                                      <button
                                        class="text-blue-600 hover:text-blue-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                        @click=${() => this.viewEmail(email)}
                                      >
                                        查看
                                      </button>
                                      <button
                                        class="text-emerald-600 hover:text-emerald-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                        @click=${() =>
                                          this.handleForward(email)}
                                      >
                                        转发
                                      </button>
                                      ${email.send_status === 1 || email.send_status === 3
                                        ? html`<button
                                            class="text-amber-600 hover:text-amber-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                            @click=${() => this.handleRetry(email)}
                                          >重试</button>`
                                        : ''}
                                      <crm-action-menu .items=${this.getActionMenuItems(email)}></crm-action-menu>
                                    </td>
                                  </tr>`,
                              )
                            : this.emails.map(
                                (email) =>
                                  html` <tr
                                    class="hover:bg-bg/50 transition-colors border-b border-border ${email.is_read ===
                                    0
                                      ? "bg-info/5"
                                      : ""}"
                                  >
                                    ${this.currentFolder === "inbox"
                                      ? html`
                                          <td class="px-4 py-2 text-center">
                                            <label
                                              class="crm-checkbox ${this.selectedEmailIds.has(
                                                email.id,
                                              )
                                                ? "checked"
                                                : ""}"
                                            >
                                              <input
                                                type="checkbox"
                                                @change=${() =>
                                                  this.toggleEmailSelection(
                                                    email.id,
                                                  )}
                                              />
                                            </label>
                                          </td>
                                        `
                                      : ""}
                                    <td class="px-4 py-2 text-center">
                                      <span
                                        class="cursor-pointer text-lg leading-none ${email.is_starred
                                          ? "text-yellow-400"
                                          : "text-text-muted/50 hover:text-yellow-400"}"
                                        @click=${(e: Event) => {
                                          e.stopPropagation();
                                          this.handleToggleStar(email);
                                        }}
                                        title=${email.is_starred
                                          ? "取消星标"
                                          : "加星标"}
                                        >${email.is_starred ? "★" : "☆"}</span
                                      >
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm max-w-[300px] relative ${email.is_read ===
                                      0
                                        ? "font-semibold text-text pl-8"
                                        : "font-normal text-text-secondary"}"
                                    >
                                      ${email.is_read === 0
                                        ? html`<span
                                            class="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-info"
                                            title="未读"
                                          ></span>`
                                        : ""}
                                      <crm-tooltip text="${email.subject || ""}"
                                        >${email.subject || "-"}</crm-tooltip
                                      >
                                      ${(email.tags || []).length > 0
                                        ? html` <div class="flex gap-1 mt-0.5">
                                            ${(email.tags || []).map(
                                              (t) => html`
                                                <span
                                                  class="inline-block px-1.5 py-0 rounded text-[10px] font-medium whitespace-nowrap"
                                                  style="background:${t.color}20;color:${t.color}"
                                                  >${t.name}</span
                                                >
                                              `,
                                            )}
                                          </div>`
                                        : ""}
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm max-w-[160px] ${email.is_read ===
                                      0
                                        ? "font-semibold text-text"
                                        : "font-normal text-text-secondary"}"
                                    >
                                      <crm-tooltip
                                        text="${this.formatAddress(
                                          email.send_from,
                                        )}"
                                        >${this.formatAddress(
                                          email.send_from,
                                        )}</crm-tooltip
                                      >
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm max-w-[160px] ${email.is_read ===
                                      0
                                        ? "font-semibold text-text"
                                        : "font-normal text-text-secondary"}"
                                    >
                                      <crm-tooltip
                                        text="${email.send_to
                                          .map((a) => this.formatAddress(a))
                                          .join(", ")}"
                                        >${email.send_to
                                          .map((a) => this.formatAddress(a))
                                          .join(", ")}</crm-tooltip
                                      >
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm text-text-muted"
                                    >
                                      ${this.formatDate(
                                        email.email_date ||
                                          (email as any).created_at,
                                      )}
                                    </td>
                                    <td
                                      class="px-4 py-2 text-sm text-text-muted"
                                    >
                                      ${email.attachment_count > 0
                                        ? html`<span
                                            class="flex items-center gap-1"
                                            ><svg
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              stroke-width="2"
                                              class="w-3.5 h-3.5"
                                            >
                                              <path
                                                d="m21.44 11.58-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 9.34l-8.54 8.54a2 2 0 0 1-2.83-2.83l8.49-8.48"
                                              /></svg
                                            >${email.attachment_count}</span
                                          >`
                                        : "-"}
                                    </td>
                                    ${this.currentFolder === "sent"
                                      ? html`<td
                                          class="px-4 py-2 text-sm text-text-muted"
                                        >
                                          ${email.tracking_id
                                            ? this.renderTrackingStatus(email)
                                            : html`<span
                                                class="text-text-muted"
                                                >-</span
                                              >`}
                                        </td>`
                                      : ""}
                                    <td class="px-4 py-2 whitespace-nowrap">
                                      <button
                                        class="text-blue-600 hover:text-blue-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                        @click=${() => this.viewEmail(email)}
                                      >
                                        查看
                                      </button>
                                      <button
                                        class="text-emerald-600 hover:text-emerald-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                        @click=${() =>
                                          this.handleForward(email)}
                                      >
                                        转发
                                      </button>
                                      <crm-action-menu .items=${this.getActionMenuItems(email)}></crm-action-menu>
                                    </td>
                                  </tr>`,
                              )}
                          ${!this.isLoading && this.emails.length === 0
                            ? html`<tr>
                                <td
                                  colspan=${this.currentFolder === "outbox"
                                    ? "6"
                                    : this.currentFolder === "inbox"
                                      ? "8"
                                      : this.currentFolder === "sent"
                                        ? "8"
                                        : "7"}
                                  class="text-center py-12 text-text-muted"
                                >
                                  暂无邮件数据
                                </td>
                              </tr>`
                            : ""}
                        </tbody>
                      </table>
                    </div>
                    <crm-pagination
                      .current=${this.currentPage}
                      .perPage=${this.perPage}
                      .total=${this.total}
                      @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
                    ></crm-pagination>
                  </div>
                </div>`
              : this.selectedThreadId
                ? html` <div class="flex-1 min-h-0 flex flex-col">
                    <div
                      class="bg-surface rounded-md shadow-lg flex flex-col flex-1 overflow-hidden relative"
                    >
                      <div
                        class="flex-shrink-0 border-b border-border px-4 py-3 flex items-center gap-3"
                      >
                        <button
                          class="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text transition-colors cursor-pointer bg-transparent border-none p-0"
                          @click=${this.closeThreadView}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="w-4 h-4"
                          >
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                          返回列表
                        </button>
                        <span class="text-text-muted">|</span>
                        <span class="font-semibold text-text text-sm"
                          >${this.threadEmails[0]?.subject || "对话"}</span
                        >
                        <span class="text-text-muted text-xs"
                          >${this.threadEmails.length} 封邮件</span
                        >
                      </div>
                      <div class="overflow-auto flex-1 min-h-0">
                        <div class="p-4 space-y-2">
                          ${this.threadEmails.map((email, i) => {
                            const isExpanded =
                              this.expandedThreadEmails[i] || false;
                            return html` <div
                              class="border border-border rounded-md overflow-hidden ${i ===
                              0
                                ? "border-l-primary border-l-2"
                                : "ml-8"}"
                            >
                              <div
                                class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-bg/50 transition-colors ${isExpanded
                                  ? "border-b border-border bg-bg/30"
                                  : ""}"
                                @click=${() => this.toggleThreadEmail(i)}
                              >
                                <div
                                  class="flex items-center gap-3 flex-1 min-w-0"
                                >
                                  <span
                                    class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex-shrink-0"
                                  >
                                    ${(
                                      email.send_from?.name ||
                                      email.send_from?.email ||
                                      "?"
                                    )
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                  <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2">
                                      ${email.is_read === 0
                                        ? html`<span
                                            class="inline-block w-2 h-2 rounded-full bg-info flex-shrink-0"
                                            title="未读"
                                          ></span>`
                                        : ""}
                                      <span
                                        class="${email.is_read === 0
                                          ? "font-semibold text-text"
                                          : "font-normal text-text-secondary"} text-sm"
                                        >${email.send_from?.name ||
                                        email.send_from?.email ||
                                        "-"}</span
                                      >
                                      <span class="text-text-muted text-xs"
                                        >${this.formatDate(
                                          email.email_date,
                                        )}</span
                                      >
                                    </div>
                                    ${!isExpanded
                                      ? html`
                                          <div
                                            class="text-text-muted text-xs mt-0.5 truncate"
                                          >
                                            ${email.subject || ""}
                                          </div>
                                        `
                                      : ""}
                                  </div>
                                </div>
                                <span
                                  class="text-text-muted text-sm flex-shrink-0 ml-2"
                                  >${isExpanded ? "▲" : "▼"}</span
                                >
                              </div>
                              ${isExpanded
                                ? html`
                                    <div class="px-4 py-3">
                                      <div
                                        class="text-sm text-text leading-relaxed"
                                      >
                                        ${email.html_content
                                          ? unsafeHTML(sanitizeEmailHtml(email.html_content))
                                          : email.content
                                            ? html`<pre
                                                class="whitespace-pre-wrap break-words m-0"
                                                style="font-family:var(--font-sans, system-ui);"
                                              >
${email.content}</pre
                                              >`
                                            : html`<p class="text-text-muted">
                                                加载中...
                                              </p>`}
                                      </div>
                                      <div
                                        class="flex gap-2 mt-3 pt-3 border-t border-border"
                                      >
                                        <button
                                          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer"
                                          @click=${(e: Event) => {
                                            e.stopPropagation();
                                            this.viewEmail(email);
                                          }}
                                        >
                                          查看详情
                                        </button>
                                      </div>
                                    </div>
                                  `
                                : ""}
                            </div>`;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>`
                : html` <div class="flex-1 min-h-0">
                    <div
                      class="bg-surface rounded-md shadow-lg flex flex-col h-full overflow-hidden relative"
                    >
                      <crm-loading-overlay
                        .active=${this.isLoading}
                      ></crm-loading-overlay>
                      <div class="overflow-auto flex-1 min-h-0">
                        <table class="w-full">
                          <thead>
                            <tr class="border-b border-border">
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider w-10"
                              ></th>
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                              >
                                主题
                              </th>
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                              >
                                参与人
                              </th>
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                              >
                                最新时间
                              </th>
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                              >
                                消息数
                              </th>
                              <th
                                class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider whitespace-nowrap"
                              >
                                操作
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            ${this.threads.map(
                              (t) =>
                                html` <tr
                                  class="hover:bg-bg/50 transition-colors border-b border-border cursor-pointer ${t.is_unread
                                    ? "bg-info/5"
                                    : ""}"
                                  @click=${() => this.viewThread(t.thread_id)}
                                >
                                  <td class="px-4 py-2 text-center">
                                    ${t.is_unread
                                      ? html`<span
                                          class="inline-block w-2 h-2 rounded-full bg-info"
                                          title="未读"
                                        ></span>`
                                      : ""}
                                  </td>
                                  <td
                                    class="px-4 py-2 text-sm max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap ${t.is_unread
                                      ? "font-semibold text-text"
                                      : "font-normal text-text-secondary"}"
                                  >
                                    ${this.truncate(t.subject, 40)}
                                  </td>
                                  <td
                                    class="px-4 py-2 text-sm text-text-secondary"
                                  >
                                    ${t.latest_sender ||
                                    t.participants.join(", ")}
                                  </td>
                                  <td class="px-4 py-2 text-sm text-text-muted">
                                    ${this.formatDate(t.latest_email_date)}
                                  </td>
                                  <td class="px-4 py-2 text-sm text-text-muted">
                                    ${t.message_count}
                                  </td>
                                  <td class="px-4 py-2 flex gap-1.5">
                                    <button
                                      class="text-blue-600 hover:text-blue-700 text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        this.viewThread(t.thread_id);
                                      }}
                                    >
                                      查看
                                    </button>
                                  </td>
                                </tr>`,
                            )}
                            ${!this.isLoading && this.threads.length === 0
                              ? html`<tr>
                                  <td
                                    colspan="6"
                                    class="text-center py-12 text-text-muted"
                                  >
                                    暂无对话数据
                                  </td>
                                </tr>`
                              : ""}
                          </tbody>
                        </table>
                      </div>
                      <crm-pagination
                        .current=${this.threadPage}
                        .perPage=${20}
                        .total=${this.threadTotal}
                        @page-change=${(e: CustomEvent) => this.handleThreadPageChange(e.detail.page)}
                      ></crm-pagination>
                    </div>
                  </div>`}
          </div>
          <!-- end main content -->
        </div>
        <!-- end sidebar+content wrapper -->

        <!-- 撰写邮件面板（compose-panel）：通过 side-panel 从底部滑出 -->
        <crm-side-panel id="compose-panel" @crm-panel-close=${this.closePanel}>
          <!-- crm-email-compose-panel 子组件负责完整的撰写邮件 UI（收件人、主题、编辑器、附件等） -->
          <crm-email-compose-panel
            .toEmail=${this.composeDraftToEmail || (this.replyingTo ? "" : "")}
            .subject=${this.replySubject}
            .customerId=${this.replyingTo?.customer_id || 0}
            .draftId=${this.composeDraftId}
            .draftContent=${this.composeDraftContent}
            .draftTo=${this.composeDraftTo}
            .draftCc=${this.composeDraftCc}
            .draftAccountId=${this.composeDraftAccountId}
            .draftAttachments=${this.composeDraftAttachments}
            .composeSession=${this._composeSession}
            .forwardHeader=${this.composeForwardHeader}
            .forwardHtml=${this.composeForwardHtml}
            @crm-email-compose-sent=${this.closePanel}
            @crm-email-compose-close=${this.closePanel}
            @crm-create-customer=${this._onCreateCustomerFromCompose}
          >
          </crm-email-compose-panel>
        </crm-side-panel>

        <!-- 快速创建客户弹窗（从撰写面板触发的邮箱未匹配到客户时显示） -->
        ${this.quickCreateEmail ? html`
          <div class="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <crm-quick-create-customer
              .email=${this.quickCreateEmail}
              @crm-customer-created=${this._onCustomerCreated}
              @crm-quick-create-cancel=${this._onQuickCreateCancel}
            ></crm-quick-create-customer>
          </div>
        ` : ""}

        <!-- 邮件详情面板（viewer-panel）：从底部滑出，左侧为邮件列表，右侧为邮件详情/内联回复 -->
        <crm-side-panel id="viewer-panel" @crm-panel-close=${this.closeViewer}>
          <div class="flex h-full">
            <!-- 左侧：邮件列表面板（1/4 宽度），允许在查看邮件时快速切换 -->
            <div class="w-1/4 flex-shrink-0">
              <email-list-panel
                .selectedEmailId=${this.selectedEmail?.id ?? null}
                .filterRead=${this.filterRead}
                .currentEmail=${this.selectedEmail}
                .currentFolder=${this.currentFolder}
              ></email-list-panel>
            </div>
            <!-- 右侧：邮件详情/内联回复区域 -->
            <div
              class="flex-1 overflow-hidden pl-3 border-l border-border flex flex-col min-h-0 relative"
            >
              <!-- 加载遮罩：获取邮件详情时显示 -->
              <crm-loading-overlay
                .active=${this.isViewingEmail}
              ></crm-loading-overlay>
              ${this.replyingTo
                ? ""
                : html`<div class="flex-1 min-h-0">
                    <!-- 邮件详情查看器（非回复状态时显示） -->
                    ${this.selectedEmail
                      ? html`<email-viewer
                          class="block h-full"
                          .email=${this.selectedEmail}
                          .isSentByMe=${isSentByMe(this.selectedEmail)}
                          @crm-email-deleted=${this.onViewerEmailDeleted}
                        ></email-viewer>`
                      : ""}
                  </div>`}
              ${this.replyingTo
                ? html`
                    <!-- 内联回复/转发区域（回复状态时显示，替代邮件详情查看器） -->
                    <div
                      class="flex-shrink-0 border-t border-border pt-3 mt-2 flex flex-col gap-2 flex-1 min-h-0"
                    >
                      <!-- 邮箱账户选择器：选择发送账户 -->
                      <div
                        class="flex items-start gap-2 text-sm border-b border-border pb-2"
                      >
                        <span
                          class="font-medium text-text-muted shrink-0 inline-block w-14 text-right mt-1.5"
                          >账户:</span
                        >
                        <crm-select
                          fullWidth
                          borderless
                          .options=${this.accounts.filter((a) => a.status === 1).map((a) => ({
                            label: a.address,
                            value: String(a.id),
                          }))}
                          .value=${this.selectedAccount !== null
                            ? String(this.selectedAccount)
                            : ""}
                          @change=${(e: CustomEvent) => {
                            const aid: number | null = e.detail.value
                              ? parseInt(e.detail.value)
                              : null;
                            this.selectedAccount = aid;
                            this.loadActiveSignature(aid);
                            if (this.isReplyAll && aid)
                              this.removeSelfFromRecipients(aid);
                          }}
                        ></crm-select>
                      </div>
                      <!-- 收件人 + Cc 图标 -->
                      <div
                        class="flex items-start gap-2 text-sm border-b border-border pb-2"
                      >
                        <span
                          class="font-medium text-text-muted shrink-0 inline-block w-14 text-right mt-1.5"
                          >收件人:</span
                        >
                        <div class="flex-1 min-w-0">
                          ${this.renderChipInput("to", true)}
                        </div>
                        <button
                          type="button"
                          class="shrink-0 text-xs text-text-muted hover:text-primary cursor-pointer bg-transparent border-none flex items-center gap-0.5 mt-1.5"
                          @click=${() => {
                            this.showCc = !this.showCc;
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="w-3.5 h-3.5"
                          >
                            <path
                              d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                            />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          <span
                            >${this.ccRecipients.length
                              ? `Cc(${this.ccRecipients.length})`
                              : "Cc"}</span
                          >
                        </button>
                      </div>
                      <!-- 抄送展开区 -->
                      ${this.showCc
                        ? html`
                            <div
                              class="flex items-start gap-2 text-sm border-b border-border pb-2"
                            >
                              <span
                                class="font-medium text-text-muted shrink-0 inline-block w-14 text-right mt-1.5"
                                >抄送:</span
                              >
                              <div class="flex-1 min-w-0">
                                ${this.renderChipInput("cc", true)}
                              </div>
                            </div>
                          `
                        : ""}
                      <!-- 主题编辑区：点击切换为输入框，支持 Enter 确认编辑 -->
                      <div
                        class="flex items-start gap-2 text-sm border-b border-border pb-2"
                      >
                        <span
                          class="font-medium text-text-muted shrink-0 inline-block w-14 text-right mt-1.5"
                          >主题:</span
                        >
                        ${this.editingSubject
                          ? html`
                              <input
                                type="text"
                                .value=${this.replySubject}
                                @input=${(e: Event) => {
                                  this.replySubject = (
                                    e.target as HTMLInputElement
                                  ).value;
                                }}
                                @blur=${() => {
                                  this.editingSubject = false;
                                }}
                                @keydown=${(e: KeyboardEvent) => {
                                  if (e.key === "Enter")
                                    this.editingSubject = false;
                                }}
                                class="flex-1 px-2 py-1 border border-border rounded text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                              />
                            `
                          : html`
                              <span
                                class="flex-1 text-text truncate cursor-pointer hover:bg-bg rounded px-2 py-1 -ml-2"
                                @click=${() => {
                                  this.editingSubject = true;
                                }}
                                >${this.replySubject || "无主题"}</span
                              >
                            `}
                      </div>
                      <!-- 编辑器与附件区域 -->
                      <div class="flex-1 min-h-0 flex flex-col">
                        <!-- 附件列表：上传中 + 已完成 -->
                        ${this.uploadingAttachments.length > 0 ||
                        this.attachedFiles.length > 0
                          ? html` <div class="flex flex-wrap gap-2 mb-2">
                              <!-- 上传中的附件：显示文件名、大小和进度条 -->
                              ${this.uploadingAttachments.map(
                                (a) =>
                                  html` <div
                                    class="flex items-center gap-1.5 px-2.5 py-1 bg-bg rounded-md border border-border text-xs"
                                  >
                                    <span
                                      class="text-text truncate max-w-[120px]"
                                      >${a.name}</span
                                    >
                                    <span class="text-text-muted shrink-0"
                                      >${formatFileSize(a.size)}</span
                                    >
                                    <div
                                      class="w-12 h-1 bg-border rounded-full overflow-hidden shrink-0"
                                    >
                                      <div
                                        class="h-full bg-primary rounded-full transition-all duration-200"
                                        style="width:${a.progress}%"
                                      ></div>
                                    </div>
                                  </div>`,
                              )}
                              <!-- 已上传完成的附件：显示文件名、大小和删除按钮 -->
                              ${this.attachedFiles.map(
                                (f, i) =>
                                  html` <div
                                    class="flex items-center gap-1.5 px-2.5 py-1 bg-info/5 rounded-md border border-info/20 text-xs"
                                  >
                                    <span
                                      class="text-text truncate max-w-[120px]"
                                      >${f.filename}</span
                                    >
                                    <span class="text-text-muted shrink-0"
                                      >${formatFileSize(f.size)}</span
                                    >
                                    <button
                                      type="button"
                                      class="p-0.5 rounded hover:bg-bg cursor-pointer text-text-muted hover:text-error transition-colors shrink-0 leading-none"
                                      @click=${() => this.removeAttachment(i)}
                                    >
                                      <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        class="w-3 h-3"
                                      >
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                      </svg>
                                    </button>
                                  </div>`,
                              )}
                            </div>`
                          : ""}
                        <!-- 富文本编辑器容器：包含编辑器和签名选择器 -->
                        <div
                          class="flex-1 min-h-0 flex flex-col border border-border rounded-md overflow-hidden focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all"
                        >
                          <div class="flex-1 min-h-0 overflow-y-auto">
                            <!-- 富文本编辑器（基于 TipTap）：转发时显示空白行，回复时显示默认空行 -->
                            <crm-rich-editor
                              name="reply-content"
                              placeholder=${this.isForward ? "输入转发内容..." : "输入回复内容..."}
                              .value=${this.isForward ? this.forwardContent : CrmEmailsPage.DEFAULT_EMPTY_LINES}
                              .noScroll=${true}
                              style="min-height:200px;flex:auto;"
                            >
                              <!-- 签名选择器：嵌入编辑器工具栏，支持切换和移除签名 -->
                              <crm-signature-selector
                                slot="toolbar-extra"
                                .accountId=${this.selectedAccount || 0}
                                @crm-signature-changed=${(e: CustomEvent) => {
                                  if (e.detail.content) {
                                    const ed = this.shadowRoot!.querySelector(
                                      "#viewer-panel",
                                    )?.querySelector("crm-rich-editor") as any;
                                    this.insertSignatureToEditor(
                                      ed,
                                      this.selectedAccount || 0,
                                      e.detail.id,
                                      e.detail.content,
                                    );
                                  }
                                }}
                                @crm-signature-removed=${() =>
                                  this.removeSignatureFromEditor()}
                              ></crm-signature-selector>
                            </crm-rich-editor>
                            <!-- 原邮件引用区域：显示引用头和原邮件内容（只读） -->
                            <div
                              class="border-t border-border px-4 py-2 text-sm shrink-0"
                              style="color:#60646c;background:#fafafa;"
                            >
                              <p class="text-xs text-text-muted mb-1">
                                ${buildQuoteCitation(this.replyingTo!)}
                              </p>
                              ${unsafeHTML(buildQuoteBodyHtml(this.replyingTo!))}
                            </div>
                          </div>
                        </div>
                      </div>
                       <!-- 回复操作按钮区：取消 / 保存草稿 / 发送 -->
                       <div class="flex justify-end gap-2 pb-1">
                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium text-text-secondary bg-bg hover:bg-border border border-border rounded transition-colors cursor-pointer"
                          @click=${this.cancelReply}
                        >
                          取消回复
                        </button>
                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium text-text-secondary border border-border bg-surface hover:bg-bg rounded transition-colors cursor-pointer"
                          @click=${this.handleReplySaveDraft}
                        >
                          保存草稿
                        </button>
                        <button
                          type="button"
                          class="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20"
                          @click=${this.handleReplySubmit}
                        >
                          发送
                        </button>
                      </div>
                    </div>
                  `
                : ""}
            </div>
          </div>
        </crm-side-panel>

        <!-- 标签管理弹窗 -->
        ${this.renderTagManager()}
        <!-- 客户详情弹窗（点击收件人 chip 中匹配到的客户时打开） -->
        <crm-customer-view-modal id="customerViewModal"></crm-customer-view-modal>
      </div>
    `;
  }
}
